import {
  address,
  contactUtils,
  payBoxUtils,
  payBoxTypes,
  personTypes,
  statusTypes,
  telephoneTypes,
  paymentTypes,
  payBoxSale,
  saleDomain,
  saleItemUtils,
  saleItemReturnedUtils,
  spendAndGetUtils,
} from '@/mixins'
import jwtLib from 'jsonwebtoken'
import { Decimal } from 'decimal.js-light'
import idbInstance from '@/libs/dexie'
import { nanoid } from 'nanoid'
import { roundDecimal } from '@/utils/number-utils'
import axiosInstance from '@/libs/axios'
import discountTypes from '@/mixins/dominio/discount-types'
import {
  localListAddItem,
  localListDeleteItem,
  localListFindIndex,
  localListUpdateItem,
} from '@/store/utils'
import isElectron from 'is-electron'
import delegablePermissions from '@/utils/delegable-permissions'
import { getAppVersions } from '@/utils/app-utils'
import {
  hasTefCardActive,
  hasTefPixActive,
  hasTefPixCanceled,
  hasTefPixActiveFailed,
} from '@/store/utils/paybox/paybox-payments'
import moment from 'moment'
import { validateBr } from 'js-brasil'
import {
  getInitialConsignReturnForm,
  getInitialPayBoxItemForm,
} from '@/mixins/dominio/pay-box-sale'
import { debounce } from 'lodash'
import EventBus, { EventBusEvents } from '@/utils/EventBus'
import { getUserData } from '@/auth/utils'
import { getApiErrorData } from '@/utils/error-utils'
import { initMonitoring, trackEvent } from '@/frontend-monitoring'
import i18n from '@/libs/i18n'

const payBoxCurrentSaleIdb = idbInstance.table('payBoxCurrentSale')
const payBoxInvoiceNumberControlIdb = idbInstance.table('payBoxInvoiceNumberControl')

const { paymentMethodInterfaceEnum, paymentTypeEnum, paymentActionTypeEnum } = paymentTypes.data()

// Função debounced para updatePdvSession - criada externamente para permitir cancelamento
const debouncedUpdatePdvSession = debounce(async (dispatch, state) => {
  console.debug(
    '[updatePdvSession] Executing, uuid:',
    state.uuid,
    'isCleaning:',
    state.isCleaning
  )
  await dispatch('upsertIdbSession')
}, 500)

export const getInitialReceiptForm = () => ({
  id: null,
  paymentMethod: {
    id: null,
    name: '',
    method: '',
  },
  paymentMethodInterface: paymentMethodInterfaceEnum.TEF,
  value: 0,
  canceled: false,
  change: 0,
  nsu: null,
  authorizationApprovalCode: null,
  documentTitular: null,
  cardBrand: null,

  // Quantidade de parcelas do cartao. 1 significa a vista.
  // ATENCAO: nao confundir com o sequencial do pagamento na venda.
  // Texto, e não número: é o que o combo de parcelamento do PDV devolve, e manter o mesmo tipo
  // ao longo do formulário é o que faz o combo exibir o rótulo da opção. Todo consumo converte
  // com Number() antes de usar.
  installmentCount: '1',
  installmentMode: null,

  // Valor da parcela apresentado ao cliente, vindo do rateio do servidor. Guardado, e nao
  // derivado na exibicao: dividir o total por N produz um numero diferente do que foi mostrado.
  installmentValue: 0,

  surchargeValue: 0,

  addedDate: null,
  canceledDate: null,
  tefTransactionFailed: false,
})

export const getInitialDeliveryForm = () => ({
  address: address.data().address,
  observation: '',
  addressSelected: 'other',
  deliveryTaxDiscountValue: 0,
  deliveryTax: 0,
  deliveryStatus: null,
})

export const getInitialPalmBeerCardData = () => ({
  customer: { id: null },
})

export const getInitialChoppGiftCardData = () => ({
  operation: null,
  cardNumber: null,
  value: null,
})

/**
 * Mutex modular para serializar execuções concorrentes do
 * `reprocessSpendAndGetRewards`. O reprocesso tem um `await` no meio
 * (enriquecimento fiscal via fetchProductByEan) e múltiplas chamadas
 * disparadas em sequência rápida (mounted da view + payBoxLoadSale +
 * setClientForm) podem se entrelaçar: a chamada B faz snapshot do
 * `state.items` antes da chamada A comitar e acaba sobrescrevendo a
 * inserção. Encadeamos via promise para garantir uma execução por vez.
 */
let reprocessSpendAndGetChain = Promise.resolve()
let reprocessSeq = 0

const getInitialState = () => ({
  id: null,
  uuid: null,
  isCleaning: false, // Flag para prevenir operações durante cleanup
  barTabConsumption: {
    id: null,
    barTab: {
      id: null,
      number: '',
    },
  },
  grossValue: 0,
  netValue: 0,
  comission: 0,
  comissionFee: null,
  paymentStarted: false,
  paymentIntegrationActive: false,
  items: [],
  /**
   * Estado do reprocessador de promoções `SpendAndGet`.
   * - `appliedTierByPromotionId`: { [promotionId]: tierId } — tier vigente
   *    da última passada do reprocessador, usada para detectar mudança de
   *    tier e limpar opt-outs.
   * - `optOutByPromotionId`: { [promotionId]: true } — promoções cujo
   *    cliente/operador removeu o brinde manualmente; o reprocessador não
   *    reinserirá brindes desta promoção até a tier mudar.
   */
  spendAndGetState: {
    appliedTierByPromotionId: {},
    optOutByPromotionId: {},
  },
  invoiceReturn: null,
  offline: false,
  priceTable: null,
  itemForm: getInitialPayBoxItemForm(),
  clientForm: {
    id: null,
    personType: personTypes.computed.personTypeEnum().CONSUMER,
    landLine: '',
    mobilePhone: '',
    email: '',
    site: '',
    observation: '',
    document: '', // represent CNPJ or CPF
    name: '', // represent name or TradingName
    priceTable: {},

    gender: '',
    birthdate: null,

    companyName: '',
    stateRegistrationType: null,
    stateRegistration: '',
    municipalInscription: '',

    address: address.data().address,
    addressList: [],

    isDocumentFromTef: false, // when customer input document in PinPad device
  },
  paymentForm: {
    genericTefTransactionFailed: false,
    discount: 0,
    rewardsAmountUsed: 0,
    receipts: [],
    change: 0,
    balance: 0,
  },
  isDelivery: false,
  isConsigned: false,
  consignedMaxReturnValue: 0,
  deliveryForm: getInitialDeliveryForm(),
  returnForm: getInitialConsignReturnForm(),
  returnSale: {
    value: 0,
  },
  palmBeerData: getInitialPalmBeerCardData(),
  choppGiftCardData: getInitialChoppGiftCardData(),
  tempDelegations: [],
  blockedPayBoxActions: [],
  electronicInvoiceRequired: false,
  electronicInvoiceInvoiceNumberGenerated: 0,
  orderChanged: false,
  coldOption: null,
  eventId: null,
  origin: null,
  suggestions: [],
  suggestionsLoading: false,
})

const appVersions = JSON.stringify(getAppVersions())

export default {
  namespaced: true,

  state: getInitialState(),

  getters: {
    isNfeRequired(state, getters, rootState, rootGetters) {
      const isEnabledNFCeOnlyForIndividual =
        rootGetters['pages/pdv/payBoxConfiguration/isEnabledNFCeOnlyForIndividual']

      return (
        state.electronicInvoiceRequired ||
        (isEnabledNFCeOnlyForIndividual &&
          state.clientForm?.personType === personTypes.computed.personTypeEnum().JURIDICAL)
      )
    },
    hasPayments(state) {
      return state.paymentForm.receipts?.length > 0
    },
    hasActivePayment(state) {
      return state.paymentForm.receipts?.filter(r => !r.canceled)?.length > 0
    },
    /**
     * Ha movimento de dinheiro na venda: um recebimento em dinheiro, ou a devolucao de uma
     * venda consignada paga em dinheiro. So nestes casos o total em dinheiro do caixa muda
     * quando a venda e finalizada.
     */
    hasCashPayment(state, getters, rootState) {
      const hasCashReceipt = state.paymentForm.receipts?.some(
        r => !r.canceled && r.paymentMethod?.method === paymentTypeEnum.CASH
      )
      if (hasCashReceipt) {
        return true
      }

      const returnPaymentMethodId = state.returnForm.paymentMethod
      if (!returnPaymentMethodId) {
        return false
      }

      return (rootState.pages?.pdv?.paymentMethodsInPdv || []).some(
        paymentMethod =>
          paymentMethod.id === returnPaymentMethodId &&
          paymentMethod.method === paymentTypeEnum.CASH
      )
    },
    hasActiveTefCardPayment(state) {
      return hasTefCardActive(state.paymentForm.receipts)
    },
    hasActiveTefPixPayment(state) {
      return hasTefPixActive(state.paymentForm.receipts)
    },
    hasCanceledTefPixPayment(state) {
      return hasTefPixCanceled(state.paymentForm.receipts)
    },
    hasActiveTefPixPaymentFailed(state) {
      return hasTefPixActiveFailed(state.paymentForm.receipts)
    },
    hasGenericTefTransactionFailed(state) {
      return state.paymentForm.genericTefTransactionFailed
    },
    getConsignedReturnData(state) {
      return {
        id: state.id,
        netValue: state.netValue,
        grossValue: state.grossValue,
        returnSale: {
          value: state.returnForm.value,
          paymentMethod: state.returnForm.paymentMethod,
          observation: state.returnForm.observation,
        },
        consigned: state.isConsigned,
        deliveryStatus: state.deliveryForm.deliveryStatus,
        consignedMaxReturnValue: state.consignedMaxReturnValue,
        pendingPaymentValue: state.pendingPaymentValue,
      }
    },
    getItems(state) {
      return state.items.filter(i => !i.isDeleted)
    },
    getPriceTable(state) {
      return state.priceTable
    },
    getOnlyItems(state) {
      return state.items.filter(i => !i.isDeleted)
    },
    getDictSaleItems(state) {
      const { getSaleProductKey } = payBoxSale.methods
      return state.items.reduce((dict, item) => {
        const { skuId, promotionId, productKitId } = item.skuProduct
        const key = getSaleProductKey({ skuId, promotionId, kitId: productKitId || 0 })

        return {
          ...dict,
          [key]: item,
        }
      }, {})
    },
    getCartItemsQuantity(state) {
      const totalQuantityItens = state.items.reduce(
        (total, item) => total + (item?.quantity ?? 0),
        0
      )

      return totalQuantityItens || 0
    },

    onlySaleCancelShouldCleanState(state, getters) {
      return getters.hasPayments
    },

    hasItems(state) {
      return state.items.filter(i => !i.isDeleted).length > 0
    },

    hasSaleItems(state) {
      return (
        state.items
          .flatMap(i => (i.skuProduct.kit ? i.kitItems : [i]))
          .filter(
            i =>
              !i.skuProduct.kit &&
              !i.isDeleted &&
              (i.classification === 'Sale' ||
                i.classification === 'Credit' ||
                i.skuProduct.classification === 'Sale' ||
                i.skuProduct.classification === 'Credit')
          )
          .filter(i => i.quantity > 0).length > 0
      )
    },

    hasLendingItems(state) {
      return (
        state.items.filter(i => (i.classification || i.skuProduct.classification) === 'Lending')
          .length > 0
      )
    },

    hasRentItems(state) {
      return (
        state.items.filter(i => (i.classification || i.skuProduct.classification) === 'Rent')
          .length > 0
      )
    },
    hasCreditItems(state) {
      return (
        state.items.filter(i => (i.classification || i.skuProduct.classification) === 'Credit')
          .length > 0
      )
    },
    hasReturnSaleItems(state) {
      return state.returnForm.returnedItems.filter(i => i.quantity > 0).length > 0
    },
    hasPalmBeerCustomer(state) {
      return !!state.palmBeerData.customer?.id
    },
    hasNewPalmBeerOperation(state, getters) {
      return getters.hasCreditItems
    },
    hasColdOptionProducts(state) {
      return state.items?.some(item => item.hasColdOption)
    },

    hasCustomer(state) {
      return state.clientForm.personType !== personTypes.computed.personTypeEnum().CONSUMER
    },
    hasRewardsApplied(state) {
      return state.paymentForm.rewardsAmountUsed > 0
    },
    canChangeCustomer(state, getters) {
      return !(getters.isOrder || getters.hasPalmBeerCustomer)
    },
    canChangePriceTable(state, getters) {
      return !getters.hasActivePayment && !getters.isOrder && !state.paymentIntegrationActive
    },

    /**
     * Tabela de preço que rege a VENDA — mesma resolução aplicada em
     * `finishSaleSaveOnServer` ao montar o payload, e portanto a mesma que
     * o backend grava em `Sale.PriceTableId`. A classificação echope da venda é
     * a `echopeClassification` desta tabela.
     *
     * Em venda de evento a tabela do evento é fixa e não cai para a efetiva do
     * canal; fora de evento vale a tabela efetiva (com fallback para a da venda).
     *
     * Depende de `effectivePriceTable/ensureDefaultPriceTable` já ter carregado a
     * default — quando ainda não carregou, o getter devolve a tabela da venda.
     */
    salePriceTable(state, getters, rootState, rootGetters) {
      const isEventSale =
        rootGetters['pages/pdv/payBoxConfiguration/currentPayboxConfiguration']?.eventId != null

      if (isEventSale) {
        return state.priceTable ?? null
      }

      const effective = rootGetters['pages/pdv/effectivePriceTable/effectivePriceTable']
      return effective ?? state.priceTable ?? null
    },

    salePriceTableId(state, getters) {
      return getters.salePriceTable?.id ?? null
    },

    isOrder(state) {
      return !!state.id && !state.barTabConsumption?.id
    },
    isChoppGiftCardConversion(state) {
      return !!state.choppGiftCardData.id && state.choppGiftCardData?.operation === 'Conversion'
    },
    isBarConsumption(state) {
      return !!state.barTabConsumption?.id
    },

    isConsignedSalePayment(state) {
      const { COMPLETED } = statusTypes.computed.deliveryStatusEnum()
      return (
        state.isConsigned &&
        (state.deliveryForm.deliveryStatus !== COMPLETED || state.pendingPaymentValue > 0)
      )
    },
    isConsignedSaleFinish(state, getters) {
      return state.isConsigned && !getters.isConsignedSalePayment
    },
    currentSaleUuid(state) {
      const { uuid } = state
      return uuid || ''
    },
    saleInProgress(state) {
      return state.items?.length > 0 || state.paymentForm?.receipts?.length > 0
    },
    getSaleData(state) {
      return {
        id: state.id,
        offline: state.offline,
        priceTable: state.priceTable,
        isDelivery: state.isDelivery,
        isConsigned: state.isConsigned,
        barTabConsumption: {
          id: state.barTabConsumption?.id || null,
          barTabNumber: state.barTabConsumption?.barTab?.number || '',
        },
      }
    },

    getSubtotalSale(state, getters) {
      return getters.getItems.reduce(
        (total, item) => total + item.priceRuleSelected.priceInfo.localNetValue,
        0
      )
    },
    getTotalSale(state, getters) {
      let total =
        getters.getSubtotalSale -
        state.paymentForm.discount -
        (state.deliveryForm.deliveryTaxDiscountValue || 0) -
        getters.getRewardsAmountUsed
      if (state.isDelivery) {
        total += state.deliveryForm.deliveryTax
      }

      if (state.comission > 0) {
        total += state.comission
      }

      return roundDecimal(total)
    },
    /** Valor efetivamente recebido, acréscimo de parcelamento incluído. */
    getTotalReceipt(state) {
      const sumReceipts = state.paymentForm.receipts.reduce((total, r) => {
        if (r.canceled) return total
        const changePaid = r.id ? r.change || 0 : 0
        return total + r.value - changePaid
      }, 0)
      return roundDecimal(sumReceipts)
    },

    /** Acréscimo de parcelamento acumulado nos recebimentos ativos. */
    getTotalInstallmentSurcharge(state) {
      return roundDecimal(
        state.paymentForm.receipts.reduce(
          (total, r) => total + (r.canceled ? 0 : Number(r.surchargeValue) || 0),
          0
        )
      )
    },

    /**
     * Parte do recebido que abate o total da venda.
     *
     * O acréscimo de parcelamento sai da conta: é cobrado a mais do cliente e não faz parte
     * do total da venda — vai à parte, em `installmentSurchargeValue`. Mantê-lo aqui zeraria
     * o saldo antes da hora e a diferença apareceria como troco, que o servidor recusa em
     * pagamento que não é dinheiro.
     */
    getTotalReceiptAppliedToSale(state, getters) {
      return roundDecimal(getters.getTotalReceipt - getters.getTotalInstallmentSurcharge)
    },

    getItemInfo(state, getters) {
      if (!getters.hasItems) {
        return { name: '', price: 0, stock: 0, productAmount: 0, subtotal: 0 }
      }

      const { skuProduct, quantity, priceRuleSelected } = getters.getItems[0]
      const { priceInfo, promotion } = priceRuleSelected

      return {
        name: skuProduct.name || '',
        price: priceInfo.unitValue || 0,
        priceWithDiscount: priceInfo.price || 0,
        stock: skuProduct.stock,
        hasPromotion: !!promotion?.id,
        promotionQuantityAvailable: promotion?.promotionQuantityAvailable,
        promotionQuantityUnlimited: promotion?.promotionQuantityUnlimited,
        productAmount: quantity,
        subtotal: priceInfo.localNetValue,
        imageSquare: skuProduct.imageSquare,
        hasDiscount: priceInfo.unitDiscountValue > 0,
      }
    },
    getSubtotalSaleWithoutDiscountInfo(state, getters) {
      return payBoxSale.methods.getSubtotalSaleWithoutDiscount(getters.getItems)
    },
    getTotalItemsDiscountInfo(state, getters) {
      // TODO utilizar método do pay-box-sale
      const result = getters.getItems.reduce(
        (total, item) => total + item.priceRuleSelected.priceInfo.localDiscountTotal,
        0
      )

      const deliveryDiscount = state.deliveryForm?.deliveryTaxDiscountValue || 0
      return roundDecimal(
        result + state.paymentForm.discount + getters.getRewardsAmountUsed + deliveryDiscount
      )
    },
    getRewardsAmountUsed(state) {
      return state.paymentForm.rewardsAmountUsed || 0
    },
    canChangeSaleValueWithRewardApplied(state, getters) {
      // se não tiver reward, não valida a regra pra reward
      if (!getters.hasRewardsApplied) return true

      return !getters.hasActivePayment
    },
    // menor quantidade permitida para o item com pagamento ativo e reward aplicado
    getItemPaidQuantity: (state, getters) => item => {
      if (getters.canChangeSaleValueWithRewardApplied) return 0
      // item salvo no servidor já existia antes do pagamento
      if (item?.id) return item.quantity
      return item?.paymentRegisteredQuantity || 0
    },
    isItemPaymentLocked: (state, getters) => item => getters.getItemPaidQuantity(item) > 0,
    getCustomerDocument(state) {
      return state.clientForm.document
    },
    getCustomerName(state) {
      return state.clientForm.name
    },
    getSuggestions(state) {
      return state.suggestions.filter(s => !s.isFeatured)
    },
    getFeaturedSuggestions(state) {
      return state.suggestions.filter(s => s.isFeatured)
    },
    isSuggestionsLoading(state) {
      return state.suggestionsLoading
    },
    clientHasValidAddressToInvoice:
      () =>
      inputAddress => {
        if (
          inputAddress.publicPlace === null ||
          inputAddress.publicPlace === '-' ||
          inputAddress.publicPlace === ''
        ) {
          return false
        }

        if (
          inputAddress.neighborhood === null ||
          inputAddress.neighborhood === '-' ||
          inputAddress.neighborhood === ''
        ) {
          return false
        }

        if (inputAddress.city === null || inputAddress.city === '-' || inputAddress.city === '') {
          return false
        }

        if (
          inputAddress.province === null ||
          inputAddress.province === '-' ||
          inputAddress.province === ''
        ) {
          return false
        }

        if (inputAddress.zipcode === null) {
          return false
        }

        return true
      },
  },

  mutations: {
    SET_INVOICE_RETURN(state, invoice) {
      state.invoiceReturn = invoice
    },
    SET_COLD_OPTION(state, coldOption) {
      state.coldOption = coldOption
    },
    SET_ITEMS(state, items) {
      const prevRewards = (state.items || [])
        .filter(it => it.isReward || (it.skuProduct?.promotionId && Number(it.netValue ?? 0) === 0))
        .length
      const nextRewards = (items || [])
        .filter(it => it.isReward || (it.skuProduct?.promotionId && Number(it.netValue ?? 0) === 0))
        .length
      if (prevRewards !== nextRewards) {
        // eslint-disable-next-line no-console
        console.log('[SAG][SET_ITEMS] rewards', { prev: prevRewards, next: nextRewards, prevTotal: state.items?.length ?? 0, nextTotal: items?.length ?? 0 })
        // eslint-disable-next-line no-console
        console.trace('[SAG][SET_ITEMS] caller stack')
      }
      state.items = items || []
    },
    SET_SPEND_AND_GET_APPLIED_TIER(state, { promotionId, tierId }) {
      if (!state.spendAndGetState.appliedTierByPromotionId) {
        state.spendAndGetState = {
          appliedTierByPromotionId: {},
          optOutByPromotionId: {},
        }
      }
      if (tierId == null) {
        delete state.spendAndGetState.appliedTierByPromotionId[promotionId]
      } else {
        state.spendAndGetState.appliedTierByPromotionId = {
          ...state.spendAndGetState.appliedTierByPromotionId,
          [promotionId]: tierId,
        }
      }
    },
    SET_SPEND_AND_GET_OPT_OUT(state, { promotionId, optOut }) {
      if (!state.spendAndGetState.optOutByPromotionId) {
        state.spendAndGetState = {
          appliedTierByPromotionId: {},
          optOutByPromotionId: {},
        }
      }
      if (optOut) {
        state.spendAndGetState.optOutByPromotionId = {
          ...state.spendAndGetState.optOutByPromotionId,
          [promotionId]: true,
        }
      } else {
        delete state.spendAndGetState.optOutByPromotionId[promotionId]
      }
    },
    CLEAN_SPEND_AND_GET_OPT_OUTS(state, { promotionIds } = {}) {
      if (!promotionIds || promotionIds.length === 0) {
        state.spendAndGetState = {
          appliedTierByPromotionId: state.spendAndGetState.appliedTierByPromotionId || {},
          optOutByPromotionId: {},
        }
        return
      }
      const next = { ...(state.spendAndGetState.optOutByPromotionId || {}) }
      promotionIds.forEach(id => delete next[id])
      state.spendAndGetState = {
        appliedTierByPromotionId: state.spendAndGetState.appliedTierByPromotionId || {},
        optOutByPromotionId: next,
      }
    },
    SET_DISCOUNT_PAYMENT_FORM(state, value) {
      state.paymentForm.discount = value
    },
    SET_SALE_PRICE_TABLE(state, priceTable) {
      state.priceTable = priceTable
    },
    SET_CLIENT_FORM(state, client) {
      state.clientForm = client
    },
    SET_ELECTRONIC_INVOICE_REQUIRED(state, value) {
      state.electronicInvoiceRequired = value
    },
    SET_PALM_BEER_DATA(state, data) {
      state.palmBeerData = data
    },
    SET_CHOPP_GIFT_CARD_DATA(state, data) {
      state.choppGiftCardData = data
    },
    SET_ALL_STATE(state, payload) {
      const incomingRewards = (payload?.items || [])
        .filter(it => it.isReward || (it.skuProduct?.promotionId && Number(it.netValue ?? 0) === 0))
        .length
      // eslint-disable-next-line no-console
      console.log('[SAG][SET_ALL_STATE]', {
        itemsCount: payload?.items?.length ?? 0,
        rewardItems: incomingRewards,
        priceTable: payload?.priceTable
          ? { id: payload.priceTable.id, active: payload.priceTable.active, activeOnline: payload.priceTable.activeOnline, activeAtm: payload.priceTable.activeAtm }
          : null,
      })
      const {
        id,
        barTabConsumption,
        offline,
        coldOption,
        priceTable,
        clientForm,
        itemForm,
        items,
        paymentForm,
        uuid,
        paymentStarted,
        deliveryForm,
        isDelivery,
        isConsigned,
        consignedMaxReturnValue,
        invoiceReturn,
        returnForm,
        grossValue,
        netValue,
        comission,
        comissionFee,
        returnSale,
        palmBeerData,
        tempDelegations,
        blockedPayBoxActions,
        choppGiftCardData,
        pendingPaymentValue,
        electronicInvoiceRequired,
        electronicInvoiceInvoiceNumberGenerated,
        origin,
      } = getInitialState()

      state.id = payload.id ?? id
      state.barTabConsumption = payload.barTabConsumption ?? barTabConsumption
      state.offline = payload.offline ?? offline
      state.coldOption = payload.coldOption ?? coldOption
      state.priceTable = payload.priceTable ?? priceTable
      state.uuid = payload.uuid ?? uuid
      state.items = payload.items ?? items
      state.itemForm = payload.itemForm ?? itemForm
      state.clientForm = { ...clientForm, ...payload.clientForm }
      state.paymentForm = payload.paymentForm ?? paymentForm
      state.paymentStarted = payload.paymentStarted ?? paymentStarted
      state.deliveryForm = payload.deliveryForm ?? deliveryForm
      state.isDelivery = payload.isDelivery ?? isDelivery
      state.isConsigned = payload.isConsigned ?? isConsigned
      state.consignedMaxReturnValue = payload.consignedMaxReturnValue ?? consignedMaxReturnValue
      state.invoiceReturn = payload.invoiceReturn ?? invoiceReturn
      state.returnForm = payload.returnForm ?? returnForm
      state.grossValue = payload.grossValue ?? grossValue
      state.netValue = payload.netValue ?? netValue
      state.pendingPaymentValue = payload.pendingPaymentValue ?? pendingPaymentValue
      state.comission = payload.comission ?? comission
      state.comissionFee = payload.comissionFee ?? comissionFee
      state.returnSale = payload.returnSale ?? returnSale
      state.palmBeerData = payload.palmBeerData ?? palmBeerData
      state.tempDelegations = payload.tempDelegations ?? tempDelegations
      state.blockedPayBoxActions = payload.blockedPayBoxActions ?? blockedPayBoxActions
      state.choppGiftCardData = payload.choppGiftCardData ?? choppGiftCardData
      state.electronicInvoiceRequired =
        payload.electronicInvoiceRequired ?? electronicInvoiceRequired
      state.electronicInvoiceInvoiceNumberGenerated =
        payload.electronicInvoiceInvoiceNumberGenerated ?? electronicInvoiceInvoiceNumberGenerated
      state.origin = payload.origin ?? origin
    },
    SET_START_PAYMENT(state) {
      state.paymentStarted = true
    },
    SET_PAYMENT_INTEGRATION_STATUS(state, isStarted) {
      state.paymentIntegrationActive = isStarted
    },
    SET_DELIVERY_DATA(state, { isDelivery, deliveryForm }) {
      const { isDelivery: defaultIsDelivery, deliveryForm: defaultDeliveryForm } = getInitialState()
      state.isDelivery = isDelivery ?? defaultIsDelivery
      state.deliveryForm = deliveryForm ?? defaultDeliveryForm
    },
    SET_RETURN_FORM(state, { returnForm }) {
      state.returnForm = returnForm
    },
    SET_BLOCKED_PAY_BOX_ACTIONS(state, { actions }) {
      state.blockedPayBoxActions = [...actions]
    },
    SET_ORDER_CHANGED(state, { changed }) {
      state.orderChanged = changed
    },
    SET_SUGGESTIONS(state, suggestions) {
      state.suggestions = suggestions || []
    },
    SET_SUGGESTIONS_LOADING(state, loading) {
      state.suggestionsLoading = loading
    },
    REMOVE_SUGGESTION(state, suggestionId) {
      state.suggestions = state.suggestions.filter(s => s.id !== suggestionId)
    },

    SET_SALE_COMISSION(state, { comissionValue, comissionFee }) {
      state.comission = comissionValue
      state.comissionFee = comissionFee
    },
    SET_PAYMENT_FORM_RECEIPTS(state, receiptList) {
      state.paymentForm.receipts = receiptList
    },
    SET_GENERIC_TEF_TRANSACTION_INTERRUPTED(state, isInterrupted) {
      state.paymentForm.genericTefTransactionFailed = isInterrupted
    },

    SET_ITEMS_PAYMENT_REGISTERED_QUANTITY(state) {
      state.items = state.items.map(i => ({ ...i, paymentRegisteredQuantity: i.quantity }))
    },
    ADD_RECEIPT(state, receipt) {
      if (receipt.paymentMethodInterface === 'Tef' && !receipt.tefPayment) {
        console.warn('Payment TEF missing tefPayment information, skipping...')
        return
      }
      state.paymentForm.receipts = localListAddItem(state.paymentForm.receipts, receipt)
    },
    REMOVE_RECEIPT(state, { index, tefPaymentCancelation, cancelationActionType }) {
      const receiptFinded = state.paymentForm.receipts[index]
      if (receiptFinded) {
        const nsu = tefPaymentCancelation?.nsu ?? receiptFinded?.nsu
        const cardBrand = tefPaymentCancelation?.cardBrand ?? receiptFinded?.cardBrand

        const receiptUpdated = {
          ...receiptFinded,
          nsu,
          cardBrand,
          tefPaymentCancelation,
          canceled: true,
          canceledDate: moment().format(),
          cancelationActionType,
        }
        state.paymentForm.receipts.splice(index, 1, receiptUpdated)
      }
    },
    CONFIRM_RECEIPT(
      state,
      { userId, receipt, confirmData: { nsu, tefPayment, confirmationActionType } }
    ) {
      const indexFound = localListFindIndex(state.paymentForm.receipts, receipt)
      const receiptFound = state.paymentForm.receipts[indexFound]
      if (!receiptFound) return

      const nsuResult =
        confirmationActionType === paymentActionTypeEnum.MANUAL ? nsu : receiptFound.nsu

      if (tefPayment && receiptFound.tefPayment && tefPayment.paymentApproved) {
        const authCode =
          (tefPayment.authorizationApprovalCode || '').trim() ||
          receiptFound.authorizationApprovalCode

        receiptFound.tefPayment = {
          ...receiptFound.tefPayment,
          transactionFailed: tefPayment.transactionFailed,
          nsu: nsuResult,
          authorizationApprovalCode: authCode,
        }
      }

      const result = {
        ...receiptFound,
        nsu: nsuResult,
        authorizationApprovalCode: receiptFound.tefPayment?.authorizationApprovalCode,
        tefTransactionFailed: false,
        confirmationActionType,
        confirmedDate: moment().format(),
        confirmedByUserId: userId,
      }

      state.paymentForm.receipts.splice(indexFound, 1, result)
    },

    ADD_TEMP_DELEGATIONS(state, tempDelegation) {
      state.tempDelegations = [...state.tempDelegations, tempDelegation]
    },

    UPDATE_BALANCE(state, { change, balance }) {
      state.paymentForm.balance = balance
      state.paymentForm.change = change
    },

    CLEAN_ITEM_FORM_META(state) {
      const { quantity, observation } = getInitialState().itemForm
      state.itemForm = { ...state.itemForm, quantity, observation }
    },
    CLEAN_CLIENT_FORM(state) {
      const { clientForm, isDelivery, deliveryForm, electronicInvoiceRequired } = getInitialState()
      state.clientForm = clientForm
      state.isDelivery = isDelivery
      state.deliveryForm = deliveryForm
      state.electronicInvoiceRequired = electronicInvoiceRequired
    },
    CLEAN_CLIENT_FORM_WITHOUT_DOCUMENT(state) {
      const { clientForm, isDelivery, deliveryForm, electronicInvoiceRequired } = getInitialState()
      const { personType, document, isDocumentFromTef } = state.clientForm
      state.clientForm = { ...clientForm, personType, document, isDocumentFromTef }
      state.isDelivery = isDelivery
      state.deliveryForm = deliveryForm
      state.electronicInvoiceRequired = electronicInvoiceRequired
    },
    CLEAN_PALM_BEER_DATA(state) {
      const { palmBeerData } = getInitialState()
      state.palmBeerData = palmBeerData
    },
    CLEAN_CHOPP_GIFT_CARD_DATA(state) {
      const { choppGiftCardData } = getInitialState()
      state.choppGiftCardData = choppGiftCardData
    },
    CLEAN_STATE(state) {
      const defaultData = getInitialState()
      Object.keys(defaultData).forEach(key => {
        state[key] = defaultData[key]
      })
    },
    SET_REWARDS_AMOUNT(state, rewardsAmountUsed) {
      state.paymentForm.rewardsAmountUsed = roundDecimal(rewardsAmountUsed, 2)
    },
  },

  actions: {
    setColdOption({ commit }, coldOption) {
      commit('SET_COLD_OPTION', coldOption)
    },
    async applyRewards({ commit, dispatch }, rewardsAmountUsed) {
      const appliedAmount = rewardsAmountUsed || 0

      if (appliedAmount > 0) {
        await initMonitoring({ isRewardAction: true })
      }

      commit('SET_REWARDS_AMOUNT', appliedAmount)
      await dispatch('updateBalance')
    },
    async loadSuggestions({ commit, state, rootGetters, getters }, cartItems) {
      try {
        commit('SET_SUGGESTIONS_LOADING', true)

        const payBoxConfiguration = rootGetters['pages/pdv/payBoxConfiguration/currentPayboxConfiguration']
        const storeId = payBoxConfiguration?.store?.id
        const eventId = payBoxConfiguration?.eventId

        const request = {
          storeId,
          eventId,
          customerId: state.clientForm?.id,
          // Em evento, a tabela do customer é apenas dado cadastral e não
          // participa da precificação.
          priceTableId: eventId == null ? state.clientForm?.priceTable?.id : null,
          items: (cartItems ?? getters.getItems).map(item => ({
            skuId: item.skuId || (item.skuProduct && item.skuProduct.skuId) || item.id,
            quantity: item.quantity || 0,
            priceTableId: item.priceTableId,
            unitValue: item.unitValue || item.price || 0,
            inputMethod: item.inputMethod,
          })),
          limit: 5,
        }

        const { data } = await axiosInstance.post(
          '/api/skus/suggestions',
          request
        )

        commit('SET_SUGGESTIONS', data || [])
      } catch (error) {
        console.error('Erro ao carregar sugestões de compra:', error)
        commit('SET_SUGGESTIONS', [])
      } finally {
        commit('SET_SUGGESTIONS_LOADING', false)
      }
    },
    async fetchClientByDocument({ dispatch, commit }, document) {
      if (!document) return
      const { LANDLINE, MOBILE } = telephoneTypes.computed.telephoneTypeEnum()

      try {
        await dispatch('pages/pdv/payBoxStatus/setStartFetchCustomer', {}, { root: true })

        const { data } = await axiosInstance.get(`/api/sales/pay-box-customer/search-by-document`, {
          params: { searchString: document || '' },
        })

        if (!data) {
          commit('CLEAN_CLIENT_FORM_WITHOUT_DOCUMENT')
        } else {
          const {
            id,
            personType,
            telephones,
            email,
            emailAccess,
            site,
            observation,
            name,
            priceTable,
            gender,
            birthdate,
            companyName,
            stateRegistrationType,
            stateRegistration,
            municipalInscription,
            addresses,
            isRewardProgramOptedIn,
          } = data

          const defaultData = getInitialState().clientForm

          const dataFormated = {}

          if (telephones?.length) {
            dataFormated.landLine = telephones.find(t => t.type === LANDLINE)?.number || ''
            dataFormated.mobilePhone = telephones.find(t => t.type === MOBILE)?.number || ''
          }

          await dispatch('setClientForm', {
            ...dataFormated,
            id,
            personType,
            email,
            emailAccess,
            site,
            observation,
            name,
            priceTable: priceTable || null,
            gender,
            birthdate,
            companyName,
            stateRegistrationType,
            stateRegistration,
            municipalInscription,
            address: addresses.length > 0 ? addresses[0] : defaultData.address,
            addressList: addresses || [],
            isRewardProgramOptedIn,
          })
        }
      } finally {
        await dispatch('pages/pdv/payBoxStatus/setFinishFetchCustomer', {}, { root: true })
      }
    },

    async fetchDeliveryTax(state, { addressData }) {
      // TODO buscar na API valor do frete
      console.log('buscar taxa de entrega para', addressData)
      return 10
    },

    async fetchPayBoxSale(state, { saleId, readOnly = false, skipTaxValidation = false }) {
      const { data } = await axiosInstance.get(`/api/sales/pay-box-sale/${saleId}`, {
        params: { readOnly, skipTaxValidation },
      })
      return payBoxSale.methods.prepareSale(data)
    },

    async searchChoppCard({ dispatch }, { cardNumber }) {
      await dispatch('cleanClientForm')

      try {
        await dispatch('pages/pdv/payBoxStatus/setStartFetchCustomer', {}, { root: true })

        const { data } = await axiosInstance.get(
          `/api/palmbeer/clients/by-card-number/${cardNumber}`
        )

        await dispatch(
          'pages/pdv/payBoxConfiguration/checkCurrentPayBoxConfigAndFetch',
          {},
          {
            root: true,
          }
        )

        if (!data.echopeCustomer) throw new Error('Dados retornados estão sem cliente.')
        await dispatch('setClientForm', data.echopeCustomer)
        await dispatch('setPalmBeerData', { customer: data })

        return data
      } finally {
        await dispatch('pages/pdv/payBoxStatus/setFinishFetchCustomer', {}, { root: true })
      }
    },

    async searchChoppGiftCardForSell({ dispatch, commit }, { cardNumber }) {
      await dispatch('cleanClientForm')
      const { data } = await axiosInstance.get(`/api/chopp-gift-cards/${cardNumber}/validate-sell`)
      commit('SET_CHOPP_GIFT_CARD_DATA', { ...data, operation: 'Sell' })
      return data
    },

    async searchChoppGiftCardForConversion({ dispatch, commit }, { cardNumber }) {
      await dispatch('cleanClientForm')
      const { data } = await axiosInstance.get(
        `/api/chopp-gift-cards/${cardNumber}/validate-conversion`
      )
      commit('SET_CHOPP_GIFT_CARD_DATA', { ...data, operation: 'Conversion' })
      return data
    },

    async loadSaleInPayBox({ state, dispatch, commit, rootGetters }, { saleId }) {
      const { clientForm, isDelivery: localIsDelivery, items: localItems, paymentForm } = state
      const haveOpenSale =
        localItems.length > 0 ||
        clientForm.document ||
        localIsDelivery ||
        paymentForm.receipts.length > 0

      if (haveOpenSale)
        throw new Error('PAY_BOX_SALE.VALIDATION_ERROR.ANOTHER_ORDER_ALREADY_LOADED')

      await dispatch('cleanState')

      // TODO substituir com dispatch('fetchPayBoxSale', {saleId})
      const { data } = await axiosInstance.get(`/api/sales/pay-box-sale/${saleId}`, {
        params: {
          skipTaxValidation: false
        }
      })
      const {
        id: saleDataId,
        uuid,
        barTabConsumption,
        customer,
        priceTable,
        discountType: saleDiscountType,
        discount: saleDiscount,
        rewardsAmountUsed,
        delivery: isDelivery,
        consigned: isConsigned,
        consignedMaxReturnValue,
        deliveryTaxDiscountValue,
        deliveryTax,
        deliveryObservation,
        deliveryAddress,
        deliveryStatus,
        offline,
        items,
        itemsReturned,
        itemKits,
        payments,
        grossValue: saleGrossValue,
        netValue: saleNetValue,
        comission,
        returnSale,
        pendingPaymentValue,
        coldOption,
        origin,
        store,
      } = data

      const { store: payBoxStore } =
        rootGetters['pages/pdv/payBoxConfiguration/currentPayboxConfiguration']
      if (store.id !== payBoxStore?.id) {
        throw new Error(i18n.t('PAY_BOX_SALE.VALIDATION_ERROR.ORDER_NOT_ALLOWED_FOR_STORE'))
      }

      // Pedido com tabela inativa deve ser ajustado no ERP (selecionando o cliente) antes de finalizar
      if (priceTable?.active === false) {
        throw new Error('PAY_BOX_SALE.PRICE_TABLE_INACTIVE')
      }

      const { cellphone, phone } = contactUtils.getPricipalMobileAndLandline(customer.telephones)
      const defaultData = getInitialState()

      const receipts = payments.map(p => ({
        ...getInitialReceiptForm(),
        ...p,
      }))
      const itemsOfSale = saleItemUtils.buildSaleItemsAndKitsFromApi(items, itemKits, itemsReturned)
      const productTotalValue = itemsOfSale.reduce(
        (total, i) => total + i.priceRuleSelected.priceInfo.localNetValue,
        0
      )

      const formatedData = {
        ...defaultData,
        id: saleDataId,
        uuid,
        barTabConsumption,
        comission,
        comissionFee: barTabConsumption?.comissionFee ?? null,
        grossValue: saleGrossValue,
        netValue: saleNetValue,
        pendingPaymentValue,
        priceTable,
        offline,
        coldOption,
        origin,
        items: itemsOfSale,
        itemsReturned,
        returnSale,
        // tipo do desconto está apenas para valor (quando vier em porcentagem, é convertido para valor)
        paymentForm: {
          ...defaultData.paymentForm,
          discount: payBoxUtils.getDiscountValue({
            discountType: saleDiscountType,
            discount: saleDiscount,
            value: productTotalValue,
          }),
          rewardsAmountUsed,
          balance: 0,
          receipts,
        },
        isDelivery,
        isConsigned,
        consignedMaxReturnValue: consignedMaxReturnValue || 0,
        deliveryForm: {
          ...defaultData.deliveryForm,
          address: deliveryAddress,
          observation: deliveryObservation,
          addressSelected: '',
          deliveryTax,
          deliveryTaxDiscountValue,
          deliveryStatus,
        },
        clientForm: {
          ...defaultData.clientForm,
          ...customer,
          landLine: phone,
          mobilePhone: cellphone,
          site: customer.siteUrl,
          address:
            customer.addresses.length > 0 ? customer.addresses[0] : defaultData.clientForm.address,
          addressList: customer.addresses || [],
        },
      }

      if (formatedData.paymentForm.rewardsAmountUsed) {
        dispatch('reward/getCustomerRewardBalance', formatedData.clientForm.id, { root: true })
      }

      if (isConsigned) {
        formatedData.returnForm = payBoxSale.methods.prepareSaleReturnedData(
          formatedData.items,
          formatedData.itemsReturned,
          returnSale
        )
      }

      commit('SET_ORDER_CHANGED', { changed: false })
      commit('SET_ALL_STATE', formatedData)
      await dispatch('updateBalance')
      await dispatch('updatePdvSession')

      // Refetch das promoções `SpendAndGet` usando a tabela EFETIVA da venda
      // recém-carregada e reprocessa o carrinho. A regra de elegibilidade é
      // baseada apenas em `effectivePriceTable` (NÃO no cliente): só vendas
      // em tabela `ActiveOnline = true` recebem brindes — e quando a tabela
      // do cliente não pode reger o canal (ATM com activeAtm=false ou
      // tabela inativa), o getter aplica o fallback para a default.
      const storeIdForPromotions = rootGetters['pages/pdv/payBoxConfiguration/currentPayboxConfiguration']?.store?.id
      if (storeIdForPromotions) {
        await dispatch(
          'pages/pdv/effectivePriceTable/ensureDefaultPriceTable',
          { storeId: storeIdForPromotions },
          { root: true }
        )
        const effective = rootGetters['pages/pdv/effectivePriceTable/effectivePriceTable']
        await dispatch(
          'pages/pdv/spendAndGetPromotions/fetchActivePromotions',
          {
            storeId: storeIdForPromotions,
            priceTableId: effective?.id ?? formatedData.priceTable?.id ?? null,
            force: true,
          },
          { root: true }
        )
        // Limpa qualquer estado residual de tier aplicada/opt-out (após cleanState
        // já está vazio, mas reforçamos para garantir que o reprocessador
        // identifique a tier como "nova" e insira os brindes esperados).
        commit('CLEAN_SPEND_AND_GET_OPT_OUTS')
      }
    },

    updatePdvSession({ dispatch, state }) {
      debouncedUpdatePdvSession(dispatch, state)
    },
    cancelUpdatePdvSession() {
      if (debouncedUpdatePdvSession && typeof debouncedUpdatePdvSession.cancel === 'function') {
        debouncedUpdatePdvSession.cancel()
        console.debug('[cancelUpdatePdvSession] Debounce canceled successfully')
      }
    },
    setDiscountPaymentForm({ commit, dispatch }, value) {
      commit('SET_DISCOUNT_PAYMENT_FORM', value)
      dispatch('updateBalance')
    },
    async setStartPayment({ commit, state }) {
      commit('SET_START_PAYMENT')
      state.items = state.items.map(i => ({ ...i, paymentStarted: true }))
    },
    async setPaymentIntegrationStatus({ commit }, isStarted) {
      commit('SET_PAYMENT_INTEGRATION_STATUS', isStarted)
    },
    async setGenericTefTransactionFailed({ commit }, tefTransactionFailed) {
      commit('SET_GENERIC_TEF_TRANSACTION_INTERRUPTED', tefTransactionFailed)
    },
    async setElectronicInvoiceRequired({ commit }, value) {
      commit('SET_ELECTRONIC_INVOICE_REQUIRED', value)
    },

    async setClientForm({ commit, state, dispatch, getters, rootGetters }, client) {
      const previousCustomerId = state.clientForm?.id ?? null
      const previousPriceTableId = state.priceTable?.id ?? null
      const payBoxConfig = rootGetters['pages/pdv/payBoxConfiguration/currentPayboxConfiguration']
      const isEventSale = payBoxConfig?.eventId != null

      const form = { ...state.clientForm, ...client }
      commit('SET_CLIENT_FORM', form)
      await dispatch('pages/pdv/saleProducts/cleanProducts', null, { root: true })

      // Em evento, a tabela configurada no evento sempre rege a venda.
      // Identificar ou trocar o cliente não pode substituí-la.
      if (
        !isEventSale &&
        getters.canChangePriceTable &&
        client.priceTable?.id !== state.priceTable?.id
      ) {
        commit('SET_SALE_PRICE_TABLE', client.priceTable)
        await dispatch('reloadItemsBySalePriceTable')
      }

      // Refetch das promoções `SpendAndGet` quando a tabela de preço da venda
      // muda (por seleção de cliente ou troca explícita de tabela). A regra é
      // baseada APENAS na tabela EFETIVA da venda — ver
      // `pages/pdv/effectivePriceTable/effectivePriceTable`. A tabela do
      // cliente não é usada como filtro. Falha silenciosa.
      const newCustomerId = state.clientForm?.id ?? null
      // Em evento, nunca usa a tabela do customer como fallback.
      const newPriceTableId =
        state.priceTable?.id ?? (isEventSale ? null : client.priceTable?.id) ?? null
      const customerChanged = newCustomerId !== previousCustomerId
      const priceTableChanged = newPriceTableId !== previousPriceTableId
      if (customerChanged || priceTableChanged) {
        const storeId = payBoxConfig?.store?.id
        if (storeId) {
          // Garante a default carregada e usa a tabela EFETIVA para o fetch
          // de promoções (cobre ATM com activeAtm=false e tabelas inativas).
          await dispatch(
            'pages/pdv/effectivePriceTable/ensureDefaultPriceTable',
            { storeId },
            { root: true }
          )
          const effective = rootGetters['pages/pdv/effectivePriceTable/effectivePriceTable']
          await dispatch(
            'pages/pdv/spendAndGetPromotions/fetchActivePromotions',
            { storeId, priceTableId: effective?.id ?? newPriceTableId },
            { root: true }
          )
          await dispatch('reprocessSpendAndGetRewards')
        }
      }
    },
    async searchAndSetClient({ commit, dispatch, getters }, { document, isDocumentFromTef }) {
      if (!getters.canChangeCustomer) {
        const alertMessage = { message: 'Não é possível alterar o cliente para esta venda' }
        EventBus.$emit(EventBusEvents.showAlert, 'warning', alertMessage)
        return null
      }

      const getDocumentType = localDocument => {
        const { CONSUMER, INDIVIDUAL, JURIDICAL } = personTypes.computed.personTypeEnum()
        const cleanedText = localDocument?.replace(/[./-]/g, '') || ''

        if (cleanedText.length < 11) {
          return CONSUMER
        }

        if (cleanedText.length === 11) {
          return INDIVIDUAL
        }

        return JURIDICAL
      }

      if (!validateBr.cpfcnpj(document || '')) {
        commit('CLEAN_CLIENT_FORM')
        return { message: 'Documento inválido' }
      }

      try {
        await dispatch('pages/pdv/payBoxStatus/setStartFetchCustomer', {}, { root: true })

        const cleanedText = document.replace(/[./-]/g, '')

        await dispatch('setClientForm', {
          document: cleanedText,
          personType: getDocumentType(cleanedText),
          isDocumentFromTef,
        })

        await dispatch('fetchClientByDocument', cleanedText)

        return null
      } finally {
        await dispatch('pages/pdv/payBoxStatus/setFinishFetchCustomer', {}, { root: true })
      }
    },

    async setPalmBeerData({ commit, state }, data) {
      const form = { ...state.palmBeerData, ...data }
      commit('SET_PALM_BEER_DATA', form)
    },

    async setRewardProgramOptedIn({ dispatch }, isOptedIn) {
      try {
        await dispatch('pages/pdv/payBoxStatus/setStartFetchCustomer', {}, { root: true })

        await dispatch('setClientForm', {
          isRewardProgramOptedIn: isOptedIn,
        })
      } finally {
        await dispatch('pages/pdv/payBoxStatus/setFinishFetchCustomer', {}, { root: true })
      }
    },

    async setDeliveryData({ commit, dispatch }, { isDelivery, deliveryForm }) {
      commit('SET_DELIVERY_DATA', { isDelivery, deliveryForm })
      await dispatch('updateBalance')
    },
    async setReturnForm({ commit, state, dispatch }, { returnForm }) {
      const { items } = state

      let resultItems = [...items]
      returnForm.returnedItems.forEach(async returnedItem => {
        const itemUpdated = saleItemUtils.updateSaleItemWithItemReturned(resultItems, returnedItem)

        if (itemUpdated) {
          resultItems = localListUpdateItem(resultItems, itemUpdated)
        }
      })

      commit('SET_ORDER_CHANGED', { changed: true })
      commit('SET_RETURN_FORM', { returnForm })

      commit('SET_ITEMS', resultItems)
      await dispatch('updateBalance')
      await dispatch('setAndCalculateComission', { comissionFee: state.comissionFee })
    },

    async addItem({ commit, dispatch, state }, { item, useStockAsMax }) {
      const { priceRuleSelected } = item
      const havePromotion = priceRuleSelected.type === payBoxUtils.priceRuleTypes.PROMOTION

      const existingItems = [...state.items]
      const itemFoundIndex = existingItems.findIndex(storedItem => {
        const hasSaleItem = storedItem.skuProduct.skuId === item.skuProduct.skuId
        const hasSaleKitItem = storedItem.skuProduct.productKitId === item.skuProduct.productKitId
        const hasSaleItemWithPromotion =
          storedItem.skuProduct.promotionId === item.skuProduct.promotionId
        const isChoppGiftCardAbsent = !storedItem.choppGiftCardNumber

        return (
          storedItem.localId &&
          hasSaleItem &&
          hasSaleKitItem &&
          hasSaleItemWithPromotion &&
          isChoppGiftCardAbsent
        )
      })
      let newItem
      let quantityWithoutPromotion = 0

      if (itemFoundIndex < 0) {
        newItem = { ...item }
      } else {
        // feito splice pra retirar da posição atual, pois dps irá ser adicionado pro inicio da lista
        const itemFound = existingItems.splice(itemFoundIndex, 1)[0]
        let oldQuantity = itemFound.quantity
        if (itemFound.isDeleted) oldQuantity -= 1
        const quantity = Number(oldQuantity) + Number(item.quantity)
        newItem = { ...itemFound, quantity, isDeleted: false }
      }

      if (havePromotion && !priceRuleSelected.promotion?.promotionQuantityUnlimited) {
        quantityWithoutPromotion =
          newItem.quantity - (priceRuleSelected.promotion?.promotionQuantityAvailable || 0)
        newItem.quantity =
          quantityWithoutPromotion > 0
            ? newItem.quantity - quantityWithoutPromotion
            : newItem.quantity
      }

      const stockQuantity =
        useStockAsMax &&
        saleItemUtils.getStockIfExceedQuantity(newItem?.skuProduct?.stock, newItem.quantity)
      if (stockQuantity) {
        newItem.quantity = stockQuantity
      }

      newItem = saleItemUtils.updateSaleItemOrSaleKit(newItem, { quantity: newItem.quantity })

      const newItems = localListAddItem(existingItems, newItem, true)

      commit('SET_ORDER_CHANGED', { changed: true })
      commit('SET_ITEMS', newItems)
      commit('REMOVE_SUGGESTION', item.skuProduct.skuId)
      await dispatch('updateBalance')
      await dispatch('setAndCalculateComission', { comissionFee: state.comissionFee })
      dispatch('loadSuggestions')

      if (havePromotion && quantityWithoutPromotion > 0) {
        await dispatch('addNewItemWithoutPromotion', {
          newItem,
          quantityWithoutPromotion,
          promotionQuantityAvailable: priceRuleSelected.promotion.promotionQuantityUnlimited
            ? null
            : priceRuleSelected.promotion.promotionQuantityAvailable,
          useStockAsMax,
        })
      }

      await dispatch('reprocessSpendAndGetRewards')
    },
    async removeItem({ commit, dispatch, state, getters, rootGetters }, item) {
      // Se o item removido é um brinde de SpendAndGet, marcar opt-out para
      // que o reprocessador não reinserte automaticamente até a tier mudar.
      const activeIds = rootGetters['pages/pdv/spendAndGetPromotions/activePromotionIds']
      const isReward = spendAndGetUtils.isSpendAndGetReward(item, activeIds)
      if (isReward) {
        const promotionId = item.skuProduct?.promotionId ?? item.promotionId
        if (promotionId) {
          commit('SET_SPEND_AND_GET_OPT_OUT', { promotionId, optOut: true })
        }
      }

      // Brindes não passam pelo `updateItem`: o `priceRuleSelected.type` é
      // `SPEND_AND_GET_REWARD`, que não existe na lista de regras avaliadas
      // por `getPriceRule`. Recalcular o preço aqui dispararia
      // "Cannot read properties of undefined (reading 'priceInfo')".
      // O reprocessador pós-remoção (`reprocessSpendAndGetRewards`) cuida
      // de manter o estado consistente.
      if (!isReward) {
        await dispatch('updateItem', { payload: { ...item, quantity: 1 } })
      }

      const newItems = localListDeleteItem(state.items, item.id || item.localId)
      commit('SET_ITEMS', newItems)

      if (!getters.hasCreditItems) {
        await dispatch('cleanPalmBeerData')
      }

      commit('SET_ORDER_CHANGED', { changed: true })
      dispatch('updateBalance')
      await dispatch('setAndCalculateComission', { comissionFee: state.comissionFee })

      await dispatch('reprocessSpendAndGetRewards')
    },
    async updateItem({ commit, state, dispatch }, { payload, useStockAsMax }) {
      let quantityWithoutPromotion = 0
      let newQuantity = Number(payload.quantity)

      const indexFound = state.items.findIndex(
        v => (v.id && v.id === payload.id) || (v.localId && v.localId === payload.localId)
      )
      const oldItem = state.items[indexFound]

      const stockQuantity =
        useStockAsMax &&
        saleItemUtils.getStockIfExceedQuantity(oldItem?.skuProduct?.stock, newQuantity)
      if (stockQuantity) {
        newQuantity = stockQuantity
      }

      let itemUpdated = saleItemUtils.updateSaleItemOrSaleKit(oldItem, {
        quantity: newQuantity,
      })

      const { priceRuleSelected } = itemUpdated
      const { promotion } = priceRuleSelected
      const havePromotionApplied = !!promotion?.id

      if (havePromotionApplied && !promotion.promotionQuantityUnlimited) {
        if (payload.id) {
          // cenário que está sendo atualizado um produto com promoção adicionado no pedido
          if (newQuantity > payload.maxQuantity) {
            await dispatch('addNewItemWithoutPromotion', {
              newItem: payload,
              quantityWithoutPromotion: newQuantity - payload.maxQuantity,
            })
            return
          }
        } else {
          quantityWithoutPromotion = newQuantity - (promotion.promotionQuantityAvailable || 0)
          newQuantity =
            quantityWithoutPromotion > 0 ? newQuantity - quantityWithoutPromotion : newQuantity

          if (itemUpdated.quantity !== newQuantity) {
            itemUpdated = saleItemUtils.updateSaleItemOrSaleKit(itemUpdated, {
              quantity: newQuantity,
            })
          }
        }
      }

      const newItems = localListUpdateItem(state.items, itemUpdated)
      commit('SET_ORDER_CHANGED', { changed: true })
      commit('SET_ITEMS', newItems)
      await dispatch('updateBalance')
      await dispatch('setAndCalculateComission', { comissionFee: state.comissionFee })

      if (havePromotionApplied && quantityWithoutPromotion > 0) {
        await dispatch('addNewItemWithoutPromotion', {
          newItem: itemUpdated,
          quantityWithoutPromotion,
          promotionQuantityAvailable: promotion.promotionQuantityUnlimited
            ? null
            : promotion.promotionQuantityAvailable,
          useStockAsMax,
        })
      }

      await dispatch('reprocessSpendAndGetRewards')
    },

    /**
     * Reprocessador de brindes `SpendAndGet`. Chamado após cada
     * mutação de itens não-brinde.
     *
     * Para cada promoção `SpendAndGet` ativa da loja:
     *   1. Calcula `eligibleAmount` (soma do `netValue` dos itens não-brinde)
     *   2. Identifica `appliedTier` (maior tier cujo `minimumAmount <= eligibleAmount`)
     *   3. Compara com a tier vigente registrada no estado:
     *      - Se mudou → limpa opt-out daquela promoção, remove brindes da tier
     *        antiga, insere brindes da nova tier
     *      - Se igual e não está em opt-out → garante que os brindes da tier
     *        estejam no carrinho
     *      - Se igual e está em opt-out → respeita; não faz nada
     *      - Se nenhuma tier elegível → remove brindes daquela promoção
     */
    /**
     * Wrapper público do reprocessador de brindes `SpendAndGet`. Serializa
     * execuções concorrentes encadeando via `reprocessSpendAndGetChain` —
     * cobre o cenário em que o mounted da view, `payBoxLoadSale` e
     * `setClientForm` disparam reprocessos quase simultâneos. Sem
     * serialização, a chamada B fazia snapshot do `state.items` antes da
     * chamada A comitar, sobrescrevendo a inserção.
     */
    reprocessSpendAndGetRewards({ dispatch }) {
      reprocessSeq += 1
      const seq = reprocessSeq
      // eslint-disable-next-line no-console
      console.log(
        `[SAG][${seq}] reprocessSpendAndGetRewards ENQUEUED`,
        new Error().stack?.split('\n').slice(2, 5).join(' | ')
      )
      const run = () => dispatch('runSpendAndGetRewardsReprocess', { seq })
      reprocessSpendAndGetChain = reprocessSpendAndGetChain
        .catch(err => {
          // eslint-disable-next-line no-console
          console.warn('[SAG] previous run errored:', err?.message || err)
        })
        .then(run)
      return reprocessSpendAndGetChain
    },

    async runSpendAndGetRewardsReprocess({ commit, dispatch, state, rootGetters }, payload = {}) {
      const seq = payload.seq ?? '?'
      // eslint-disable-next-line no-console
      console.log(`[SAG][${seq}] START`, {
        itemsCount: state.items.length,
        rewardItems: state.items
          .filter(it => it.isReward || (it.skuProduct?.promotionId && Number(it.netValue ?? it.priceRuleSelected?.priceInfo?.localNetValue ?? 0) === 0))
          .map(it => ({ id: it.id, isReward: it.isReward, skuId: it.skuProduct?.skuId, promotionId: it.skuProduct?.promotionId ?? it.promotionId, qty: it.quantity })),
      })
      const activePromotions =
        rootGetters && rootGetters['pages/pdv/spendAndGetPromotions/promotionsById']
          ? Object.values(rootGetters['pages/pdv/spendAndGetPromotions/promotionsById'])
          : []

      // Predicate compartilhado: identifica qualquer item do carrinho que seja
      // brinde da promocao em questao, seja recem-inserido (flag local) ou
      // recuperado de uma venda persistida.
      const buildIsRewardOfPromotion = promotionId => it => {
        const itemPromotionId = it.skuProduct?.promotionId ?? it.promotionId
        if (itemPromotionId !== promotionId) return false
        if (it.isReward) return true
        const localNetValue =
          it.priceRuleSelected?.priceInfo?.localNetValue ?? it.netValue ?? 0
        return Number(localNetValue) === 0
      }

      // Regra de elegibilidade da venda para promoções `SpendAndGet`:
      //   1. A venda não pode estar associada a um evento.
      //   2. A tabela de preço EFETIVA da venda deve ter `activeOnline = true`.
      //   3. A venda não pode ser consignada (`Sales.Consigned = 1`). Vendas
      //      consignadas criadas no ERP e carregadas no PDV não recebem brindes
      //      de promoções SpendAndGet.
      //
      // O getter de tabela aplica fallback para a default quando a tabela atual
      // não pode reger o canal (ex.: ATM com activeAtm=false, tabela inativa).
      // Quando `effectivePriceTable` ainda é nula (bootstrap inicial), confiamos
      // na lista carregada pelo backend. A regra de evento, por outro lado, é
      // aplicada imediatamente e o bail-out remove brindes locais remanescentes.
      const eventId = rootGetters['pages/pdv/payBoxConfiguration/currentPayboxConfiguration']?.eventId
      const isEventSale = eventId != null
      const isConsignedSale = state.isConsigned === true
      const effectivePriceTable =
        rootGetters['pages/pdv/effectivePriceTable/effectivePriceTable'] ?? null
      const blockSpendAndGet =
        isEventSale ||
        isConsignedSale ||
        (effectivePriceTable !== null && effectivePriceTable.activeOnline !== true)

      // eslint-disable-next-line no-console
      console.log(`[SAG][${seq}] context`, {
        activePromotionsCount: activePromotions.length,
        eventId,
        isEventSale,
        effectivePriceTable: effectivePriceTable
          ? { id: effectivePriceTable.id, active: effectivePriceTable.active, activeOnline: effectivePriceTable.activeOnline, activeAtm: effectivePriceTable.activeAtm }
          : null,
        salePriceTable: state.priceTable
          ? { id: state.priceTable.id, active: state.priceTable.active, activeOnline: state.priceTable.activeOnline, activeAtm: state.priceTable.activeAtm }
          : null,
        blockSpendAndGet,
      })

      // Quando a lista de promoções ativas é vazia, a venda pertence a um
      // evento ou a tabela da venda não é Online, garantimos que nenhum
      // brinde local permaneça no carrinho.
      if (!activePromotions.length || blockSpendAndGet) {
        const isLocalReward = it => it.isReward && !it.id
        const localRewardsToRemove = state.items.filter(isLocalReward)
        let bailOutReason = 'price_table_not_online'
        if (isEventSale) bailOutReason = 'event_sale'
        else if (isConsignedSale) bailOutReason = 'consigned_sale'
        else if (!activePromotions.length) bailOutReason = 'no_active_promotions'

        if (localRewardsToRemove.length > 0) {
          // eslint-disable-next-line no-console
          console.warn(`[SAG][${seq}] BAIL-OUT removing ${localRewardsToRemove.length} local rewards`, {
            reason: bailOutReason,
            removed: localRewardsToRemove.map(it => ({ skuId: it.skuProduct?.skuId, promotionId: it.skuProduct?.promotionId ?? it.promotionId })),
          })
          const cleanedItems = state.items.filter(it => !isLocalReward(it))
          commit('SET_ITEMS', cleanedItems)
        } else {
          // eslint-disable-next-line no-console
          console.log(`[SAG][${seq}] BAIL-OUT no-op (nothing to remove)`)
        }
        // Limpa estado de tier aplicada e opt-out remanescente (não há mais
        // promoções vigentes/aplicáveis para este pedido).
        const promotionIdsInState = [
          ...Object.keys(state.spendAndGetState?.appliedTierByPromotionId || {}),
          ...Object.keys(state.spendAndGetState?.optOutByPromotionId || {}),
        ]
        promotionIdsInState.forEach(idStr => {
          const promotionId = Number(idStr)
          commit('SET_SPEND_AND_GET_APPLIED_TIER', { promotionId, tierId: null })
          commit('SET_SPEND_AND_GET_OPT_OUT', { promotionId, optOut: false })
        })
        return
      }

      const activeIds = new Set(activePromotions.map(p => p.id))
      const eligibleAmount = spendAndGetUtils.calculateEligibleAmount(state.items, activeIds)

      const appliedByPromotion = state.spendAndGetState?.appliedTierByPromotionId || {}
      const optOutByPromotion = state.spendAndGetState?.optOutByPromotionId || {}

      // Etapa 1 (sincrona): aplica mutacoes de estado por promocao
      // (opt-out/applied-tier) e remove brindes locais quando a tier mudou.
      // Coleta os brindes que ainda precisam ser inseridos para enriquecer
      // em paralelo na etapa 2.
      let nextItems = [...state.items]
      const rewardsToBuild = []

      activePromotions.forEach(promotion => {
        const newTier = spendAndGetUtils.selectAppliedTier(promotion, eligibleAmount)
        const newTierId = newTier?.id ?? null
        const previousTierId = appliedByPromotion[promotion.id] ?? null
        const tierChanged = previousTierId !== newTierId
        const isRewardOfThisPromotion = buildIsRewardOfPromotion(promotion.id)

        if (tierChanged) {
          if (optOutByPromotion[promotion.id]) {
            commit('SET_SPEND_AND_GET_OPT_OUT', { promotionId: promotion.id, optOut: false })
          }
          nextItems = nextItems.filter(it => !(isRewardOfThisPromotion(it) && !it.id))
          commit('SET_SPEND_AND_GET_APPLIED_TIER', { promotionId: promotion.id, tierId: newTierId })
        }

        if (!newTier) return

        if (!tierChanged && state.spendAndGetState.optOutByPromotionId[promotion.id]) {
          return
        }

        const existingRewardSkuIds = new Set(
          nextItems
            .filter(isRewardOfThisPromotion)
            .map(it => it.skuProduct?.skuId ?? it.skuProduct?.id)
        )

        newTier.rewards.forEach(reward => {
          if (existingRewardSkuIds.has(reward.skuId)) return
          if (!reward.sku || reward.sku.price == null) return
          // Pula brindes sem estoque suficiente para a quantidade configurada.
          // Cobre o cenario em que a tier eh atingida mas o reward esta
          // indisponivel — nao queremos inserir item invalido nem mostrar
          // "ganhou um brinde" para algo que nao podemos entregar.
          const availableStock = Number(reward.sku.stock ?? 0)
          if (availableStock < Number(reward.quantity)) return
          const built = spendAndGetUtils.buildRewardSaleItem(promotion, newTier, reward, {
            priceTableId: state.priceTable?.id ?? 1,
          })
          rewardsToBuild.push({ built, ean: reward.sku.ean })
        })
      })

      // eslint-disable-next-line no-console
      console.log(`[SAG][${seq}] etapa1 result`, {
        rewardsToBuildCount: rewardsToBuild.length,
        rewardsToBuild: rewardsToBuild.map(r => ({ skuId: r.built.skuProduct?.skuId, ean: r.ean })),
        nextItemsCount: nextItems.length,
      })

      // Etapa 2 (assincrona, paralela): enriquece cada brinde com os dados
      // fiscais completos do SKU (classification + tax) via fetchProductByEan.
      // Sem isso, o item-brinde nao passa pelo filtro de `generateInvoice` e
      // fica de fora do XML do cupom fiscal.
      const uniqueEans = Array.from(
        new Set(rewardsToBuild.map(r => r.ean).filter(ean => !!ean))
      )

      const fetchByEan = ean =>
        dispatch(
          'pages/pdv/saleProducts/fetchProductByEan',
          {
            ean,
            customerId: state.clientForm?.id,
            priceTableId: state.priceTable?.id,
            includePromotion: false,
          },
          { root: true }
        ).catch(err => {
          // eslint-disable-next-line no-console
          console.warn(
            '[reprocessSpendAndGetRewards] fetchProductByEan failed for ean',
            ean,
            err?.message || err
          )
          return null
        })

      const fetchedSkus = await Promise.all(uniqueEans.map(fetchByEan))
      const enrichedByEan = uniqueEans.reduce((acc, ean, idx) => {
        acc[ean] = fetchedSkus[idx]
        return acc
      }, {})

      // Etapa 3: aplica enriquecimentos e adiciona ao carrinho.
      rewardsToBuild.forEach(({ built, ean }) => {
        const fullSku = ean ? enrichedByEan[ean] : null
        const enrichedItem = fullSku
          ? {
              ...built,
              classification: fullSku.classification ?? built.classification,
              skuProduct: {
                ...built.skuProduct,
                ...fullSku,
                // Preserva os campos de identidade/preco do brinde, que podem
                // diferir do retorno padrao do endpoint (preco zerado de venda,
                // referencia ao promotionId, etc).
                skuId: built.skuProduct.skuId,
                promotionId: built.skuProduct.promotionId,
                price: built.skuProduct.price,
                originalPrice: built.skuProduct.originalPrice,
                unitValue: built.skuProduct.unitValue,
                kit: false,
                productKitId: null,
              },
            }
          : built
        nextItems = localListAddItem(nextItems, enrichedItem, true)
      })

      // Compromete apenas se houve diferença (evita re-render desnecessário).
      const before = state.items
      let didCommit = false
      let commitReason = null
      if (nextItems !== before && nextItems.length !== before.length) {
        commit('SET_ITEMS', nextItems)
        didCommit = true
        commitReason = 'length_diff'
      } else {
        // mesmo length, pode ter ocorrido remoção+inserção balanceada — comparar referências
        const same =
          nextItems.length === before.length &&
          nextItems.every((it, idx) => it === before[idx])
        if (!same) {
          commit('SET_ITEMS', nextItems)
          didCommit = true
          commitReason = 'ref_diff'
        }
      }
      // eslint-disable-next-line no-console
      console.log(`[SAG][${seq}] END`, {
        didCommit,
        commitReason,
        beforeCount: before.length,
        afterCount: nextItems.length,
        finalRewards: nextItems
          .filter(it => it.isReward || (it.skuProduct?.promotionId && Number(it.netValue ?? it.priceRuleSelected?.priceInfo?.localNetValue ?? 0) === 0))
          .map(it => ({ id: it.id, isReward: it.isReward, skuId: it.skuProduct?.skuId })),
      })
    },

    async addNewItemWithoutPromotion(
      { state, dispatch },
      { newItem, quantityWithoutPromotion, promotionQuantityAvailable, useStockAsMax }
    ) {
      const { clientForm, priceTable } = state

      const product = await dispatch(
        'pages/pdv/saleProducts/fetchProductByEan',
        {
          ean: newItem.skuProduct.ean,
          customerId: clientForm?.id,
          priceTableId: priceTable?.id,
          includePromotion: false,
        },
        { root: true }
      )

      if (promotionQuantityAvailable && product?.stock) {
        product.stock -= promotionQuantityAvailable
      }

      const newItemBuilded = saleItemUtils.buildProductToSaleItem(
        {
          ...product,
          inputMethod: newItem.inputMethod,
          palmBeerCardNumber: newItem.palmBeerCardNumber,
          choppGiftCardNumber: newItem.choppGiftCardNumber,
          choppGiftCardId: newItem.choppGiftCardId,
        },
        {
          replicatedWithoutPromotion: true,
          quantity: quantityWithoutPromotion,
          inputMethod: newItem.inputMethod,
          palmBeerCardNumber: newItem.palmBeerCardNumber,
          choppGiftCardNumber: newItem.choppGiftCardNumber,
          choppGiftCardId: newItem.choppGiftCardId,
        }
      )

      await dispatch('addItem', { item: newItemBuilded, useStockAsMax })
    },

    async reloadItemsBySalePriceTable({ state, getters, commit, dispatch, rootGetters }) {
      const { store: payBoxStore } =
        rootGetters['pages/pdv/payBoxConfiguration/currentPayboxConfiguration']

      try {
        console.log('[ReloadByPriceTable] START')

        await dispatch('pages/pdv/payBoxStatus/setStartSaleItemsReloadPrice', {}, { root: true })
        const activeSpendAndGetPromotionIds = rootGetters['pages/pdv/spendAndGetPromotions/activePromotionIds']

        const productsPromise = state.items.map(async saleItem => {
          const isCredit = saleItemUtils.isCreditSaleItem(saleItem)
          const isSpendAndGetPromotion = spendAndGetUtils.isSpendAndGetReward(saleItem,activeSpendAndGetPromotionIds)

          if (isSpendAndGetPromotion || isCredit || saleItem.id) return saleItem

          const { data } = await axiosInstance.get(
            `/api/sales/pay-box-sku/store/${payBoxStore.id}/ean/${saleItem.skuProduct.ean}`,
            {
              params: {
                priceTableId: state.priceTable?.id,
                includePromotion: !saleItem.replicatedWithoutPromotion,
              },
            }
          )

          const refreshedSaleItem = saleItemUtils.saleItemForceRefreshUsingProduct(saleItem, data)

          return refreshedSaleItem
        })

        const itemsUpdated = await Promise.all(productsPromise)

        if (!getters.canChangePriceTable) {
          console.log('[ReloadByPriceTable] CANCELED')
          return
        }

        commit('SET_ITEMS', itemsUpdated)
        await dispatch('updateBalance')
        await dispatch('setAndCalculateComission', { comissionFee: state.comissionFee })

        console.log('[ReloadByPriceTable] FINISHED')
      } finally {
        await dispatch('pages/pdv/payBoxStatus/setFinishSaleItemsReloadPrice', {}, { root: true })
      }
    },

    async setAndCalculateComission({ commit, getters, dispatch }, { comissionFee }) {
      let comissionValue = 0
      if (comissionFee > 0) {
        // getSubtotalSale is the netValue
        comissionValue = roundDecimal(getters.getSubtotalSale * comissionFee, 2)
      }

      commit('SET_SALE_COMISSION', { comissionValue, comissionFee: comissionFee || null })
      await dispatch('updateBalance')
    },

    async addChoppGiftCardReceipt({ commit, dispatch, getters }) {
      const { data } = await axiosInstance.get(
        '/api/settings/financial/payment-methods/chopp-gift-card',
        { params: { pageSize: 0, pageIndex: 0 } }
      )
      const receipt = getInitialReceiptForm()
      receipt.value = getters.getTotalSale
      receipt.paymentMethod.id = data.id
      receipt.paymentMethod.method = data.method
      receipt.paymentMethod.name = data.name
      receipt.paymentMethodInterface = 'Manual'
      commit('SET_ITEMS_PAYMENT_REGISTERED_QUANTITY')
      commit('ADD_RECEIPT', receipt)
      dispatch('updateBalance')
    },

    async validateAddReceipt({ state, getters, rootGetters }) {
      const result = { title: '', message: '' }

      const isTefCustomerDocumentWaiting =
        rootGetters['pages/pdv/payBoxTefRequestData/isTefCustomerDocumentWaiting']
      if (isTefCustomerDocumentWaiting) {
        result.title = 'Aguarde um momento'
        result.message = 'Por favor, aguarde até que o cliente informe o documento no PINPAD.'

        return result
      }

      const getIsSaleItemsReloading = rootGetters['pages/pdv/payBoxStatus/getIsSaleItemsReloading']
      if (getIsSaleItemsReloading) {
        result.title = 'Aguarde um momento'
        result.message =
          'Por favor, aguarde enquanto verificamos se há benefícios disponíveis para esta venda.'

        return result
      }

      const getIsCustomerFetching = rootGetters['pages/pdv/payBoxStatus/getIsCustomerFetching']
      if (getIsCustomerFetching) {
        result.title = 'Aguarde um momento'
        result.message = 'Por favor, aguarde enquanto buscamos as informações do cliente.'

        return result
      }

      if (getters.hasActiveTefPixPaymentFailed) {
        result.title = 'Pagamentos'
        result.message = 'PAY_BOX_SALE.PAYMENT.HAS_PAYMENT_WITH_PENDING_ACTION'
      } else if (getters.getTotalSale <= 0) {
        result.title = 'Venda'
        result.message = 'Total da venda está zerado'
      } else if (state.paymentForm.balance === 0) {
        result.title = 'Venda'
        result.message = 'Saldo a pagar está zerado'
      }

      return result
    },
    addReceipt({ commit, dispatch }, receipt) {
      commit('SET_ORDER_CHANGED', { changed: true })
      commit('SET_ITEMS_PAYMENT_REGISTERED_QUANTITY')
      commit('ADD_RECEIPT', receipt)
      dispatch('updateBalance')
    },
    removeReceipt({ commit, dispatch }, { index, tefPaymentCancelation, cancelationActionType }) {
      commit('SET_ORDER_CHANGED', { changed: true })
      commit('REMOVE_RECEIPT', { index, tefPaymentCancelation, cancelationActionType })
      dispatch('updateBalance')
    },
    confirmReceipt({ commit }, { receipt, confirmData, userDelegated }) {
      let userId = getUserData()?.userId

      if (userDelegated?.token) {
        userId = jwtLib.decode(userDelegated.token)?.nameid || userId
      }

      commit('CONFIRM_RECEIPT', {
        userId,
        receipt,
        confirmData,
      })
    },
    async printReceipt({ state, getters, dispatch, rootGetters }, { index }) {
      await dispatch(
        'pages/pdv/payBoxConfiguration/checkCurrentPayBoxConfigAndFetch',
        {},
        {
          root: true,
        }
      )

      const printerConfig =
        rootGetters['pages/pdv/payBoxConfiguration/thermalPrinterAgentConfigPayload']

      const payboxConfig = rootGetters['pages/pdv/payBoxConfiguration/currentPayboxConfiguration']
      const isPdvAtm = rootGetters['pages/pdv/payBoxConfiguration/isPdvAtm']

      const { id: saleId, uuid: saleUuid } = state

      const { receipts } = state.paymentForm
      const receipt = index === -1 ? receipts[receipts.length - 1] : receipts[index]

      const { tefPayment, tefPaymentCancelation } = receipt

      const isManualPix =
        receipt.paymentMethod.method === paymentTypeEnum.PIX &&
        receipt.paymentMethodInterface === paymentMethodInterfaceEnum.MANUAL

      const isManualConfirmation = receipt.confirmationActionType === paymentActionTypeEnum.MANUAL

      const tefPayload = receipt.canceled ? tefPaymentCancelation : tefPayment
      const isTefCouponPrint = !isManualConfirmation && tefPayload?.couponCustomer

      if (isTefCouponPrint) {
        const couponStore = {
          printText: tefPayload.couponStore,
          template: 'PAYMENT_GETNET',
        }
        const couponCustomer = {
          printText: tefPayload.couponCustomer,
          template: 'PAYMENT_GETNET',
        }

        const printTexts = isPdvAtm ? [couponCustomer] : [couponStore, couponCustomer]

        try {
          await window.electronAPI.print({
            ...printerConfig,
            request: {
              openCashDrawer: false,
              printTexts,
            },
          })
        } catch (error) {
          console.error('error on print tef', error)
        }
      } else if (isManualPix || isManualConfirmation || (tefPayload && !isTefCouponPrint)) {
        try {
          await window.electronAPI.print({
            ...printerConfig,
            request: {
              openCashDrawer: false,
              printTexts: [
                {
                  printText: JSON.stringify({
                    title: 'Comprovante PIX',
                    copyNumbers: 1,
                    saleId: (saleId || saleUuid).toString(),
                    payBox: payboxConfig,
                    ...receipt,
                  }),
                  template: 'PAYMENT_GENERIC',
                },
              ],
            },
          })
        } catch (error) {
          console.error('error on print generic payment - pix', error)
        }
      } else if (getters.isConsignedSalePayment) {
        try {
          await window.electronAPI.print({
            ...printerConfig,
            request: {
              openCashDrawer: false,
              printTexts: [
                {
                  printText: JSON.stringify({
                    saleId: (saleId || saleUuid).toString(),
                    payBox: payboxConfig,
                    ...receipt,
                  }),
                  template: 'PAYMENT_GENERIC',
                },
              ],
            },
          })
        } catch (error) {
          console.error('error on print generic payment - consigned', error)
        }
      }
      // else if (state.paymentForm?.change > 0) {
      //   try {
      //     await window.electronAPI.openCashDrawer(printerConfig)
      //   } catch (error) {
      //     /* eslint-disable no-console */
      //     console.error('error on open cash drawer', error)
      //   }
      // }
    },

    async updateBalance({ commit, getters, dispatch }) {
      const result = roundDecimal(getters.getTotalSale - getters.getTotalReceiptAppliedToSale)
      let change = 0
      let balance = 0

      if (result < 0) {
        change = Math.abs(result)
      } else {
        balance = result
      }

      commit('UPDATE_BALANCE', { change, balance })
      await dispatch('updateReceiptChanges', { change })
    },
    updateReceiptChanges({ state, commit }, { change }) {
      const cashReceipts = state.paymentForm.receipts.filter(
        r => !r.canceled && !r.id && r.paymentMethod?.method === paymentTypeEnum.CASH
      )
      if (cashReceipts.length > 0) {
        cashReceipts.forEach(r => ({ ...r, change: 0 }))

        const lastReceiptIndex = cashReceipts.length - 1
        cashReceipts[lastReceiptIndex] = { ...cashReceipts[lastReceiptIndex], change }

        let receiptsList = state.paymentForm.receipts
        cashReceipts.forEach(r => {
          receiptsList = localListUpdateItem(receiptsList, r)
        })
        commit('SET_PAYMENT_FORM_RECEIPTS', receiptsList)
      }
    },

    /**
     * Validation used in ATM and PDV
     */
    async validateFinishSale({ state, dispatch, getters }) {
      const result = {
        title: '',
        message: '',
        isInvoiceExceededValueLimits: false,
      }

      result.title = 'Dados inválidos no pagamento'
      if (getters.hasActiveTefPixPaymentFailed) {
        result.message = 'PAY_BOX_SALE.PAYMENT.HAS_PAYMENT_WITH_PENDING_ACTION'
        return result
      }

      if (state.paymentForm.balance > 0) {
        result.message = 'Há recebimentos faltando no pagamento'
        return result
      }

      result.title = 'Dados inválidos no carrinho'
      if (state.items.length === 0) {
        result.message = 'Não foi adicionado nenhum item'
        return result
      }

      result.title = ''
      result.isInvoiceExceededValueLimits = await dispatch(
        'pages/pdv/payBoxInvoice/validateInvoiceExceededLimits',
        { totalSaleValue: getters.getTotalSale },
        { root: true }
      )
      if (result.isInvoiceExceededValueLimits) {
        if (!getters.hasCustomer) {
          result.message =
            'Cliente é obrigatório para emissão de cupom fiscal devido ao valor total da venda'
          return result
        }

        if (
          getters.isNfeRequired &&
          state.clientForm.address &&
          !getters.clientHasValidAddressToInvoice(state.clientForm.address)
        ) {
          result.message =
            'Cliente informado não possui endereço ou o mesmo é inválido! Devido ao valor total da venda o endereço é uma informação obrigatória!'
          return result
        }
      }

      return result
    },

    async finishSale({ dispatch, state, getters, rootGetters }, coldOption) {
      await dispatch(
        'pages/pdv/payBoxConfiguration/checkCurrentPayBoxConfigAndFetch',
        {},
        {
          root: true,
        }
      )

      const payBoxConfig =
        rootGetters['pages/pdv/payBoxConfiguration/currentPayboxConfiguration']
      state.coldOption =
        payBoxConfig?.eventId != null
          ? saleDomain.data().coldOptionEnum.COLD
          : state.coldOption ?? coldOption
      let invoiceResult = {}
      const shouldResubmitNfe =
        state.invoiceReturn &&
        state.invoiceReturn.model === 'NFE' &&
        state.invoiceReturn.status === 'REJECTED'
      if (
        (!state.isConsigned || getters.isConsignedSaleFinish) &&
        getters.hasSaleItems &&
        isElectron() &&
        (!state.invoiceReturn || shouldResubmitNfe) &&
        !getters.isChoppGiftCardConversion
      ) {
        if (getters.isNfeRequired) {
          invoiceResult = await dispatch('generateNFe')
        } else {
          invoiceResult = await dispatch('generateInvoice')
        }
      }

      const createdSale = await dispatch('finishSaleSaveOnServer')
      // devemos tomar cuidado adicionando métodos depois do `finishSaleSaveOnServer`, pois podemos salvar a venda e não continuar o fluxo até limpar a venda no PDV

      const printerConfig =
        rootGetters['pages/pdv/payBoxConfiguration/thermalPrinterAgentConfigPayload']

      try {
        window.electronAPI.openCashDrawer(printerConfig)
      } catch (err) {
        console.error(err)
      }

      // ATM: o comprovante do pagamento na TEF deve ser o ÚLTIMO documento impresso,
      // após o cupom fiscal e o recibo de retirada. Por isso, no ATM os recibos de
      // pagamento não são impressos no momento do recebimento (ver AtmPayment.vue);
      // são impressos aqui, ao final, na ordem dos recebimentos.
      const isPdvAtm = rootGetters['pages/pdv/payBoxConfiguration/isPdvAtm']
      if (isPdvAtm) {
        const { receipts } = state.paymentForm
        for (let index = 0; index < (receipts?.length ?? 0); index += 1) {
          if (!receipts[index].canceled) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await dispatch('printReceipt', { index })
            } catch (err) {
              console.error('print tef receipt (deferred):', err)
            }
          }
        }
      }

      return {
        ...invoiceResult,
        createdSale,
      }
    },

    async finishSaleSaveOnServer({ dispatch, state, getters, rootGetters }) {
      const {
        store: payBoxStore,
        id: payBoxId,
        eventId,
        isAtm,
        pickupPoint,
      } = rootGetters['pages/pdv/payBoxConfiguration/currentPayboxConfiguration']

      const {
        id: saleId,
        priceTable: salePriceTable,
        uuid,
        items,
        clientForm,
        paymentForm,
        isDelivery,
        deliveryForm,
        invoiceReturn,
        returnForm,
        tempDelegations,
        comission,
        coldOption,
        origin,
      } = state // getInitialState()
      const { INDIVIDUAL, JURIDICAL } = personTypes.computed.personTypeEnum()
      const customerTypeEnum = {
        CONSUMER: 'Consumer',
        NEW_CUSTOMER: 'NewCustomer',
        EXISTING_CUSTOMER: 'ExistingCustomer',
      }

      let customer = {
        customerType: customerTypeEnum.CONSUMER,
        id: clientForm?.id || null,
      }
      if (getters.isBarConsumption && !customer.id) {
        customer.id = 1
      }
      if (clientForm.personType === INDIVIDUAL || clientForm.personType === JURIDICAL) {
        if (clientForm.id) {
          customer = {
            customerType: customerTypeEnum.EXISTING_CUSTOMER,
            name: clientForm.name?.trim(),
            id: clientForm.id,
            companyName: clientForm.companyName?.trim(),
          }
        } else {
          // FIXME usar Array.some()
          let hasAddress = false
          Object.keys(clientForm.address).forEach(a => {
            if (clientForm.address[a]) {
              hasAddress = true
            }
          })

          customer = {
            customerType: customerTypeEnum.NEW_CUSTOMER,
            id: null,
            personType: clientForm.personType,
            stateRegistration: clientForm.stateRegistration,
            stateRegistrationType: clientForm.stateRegistrationType,
            document: clientForm.document,
            name: clientForm.name?.trim(),
            email: clientForm.email?.trim(),
            observation: clientForm.observation,
            birthdate: clientForm.birthdate,
            landlinePhone: clientForm.landLine,
            mobilePhone: clientForm.mobilePhone,
            address: hasAddress ? clientForm.address : null,
            companyName: clientForm.companyName?.trim(),
          }
        }
      }

      const saleItemsToSave = items
        .flatMap(item => {
          if (item.kitItems?.length > 0) return item.kitItems

          return [item]
        })
        .flatMap(item => {
          if (item.lendingProducts?.length > 0) return [item, ...item.lendingProducts]

          return [item]
        })
        .map(item => {
          const { priceRuleSelected } = item
          const { priceTable, productKit, promotion, tieredPrice, priceInfo } = priceRuleSelected

          return {
            id: item.id,
            skuId: item.skuProduct.skuId,

            productKitId: productKit?.id,
            saleItemKitId: item.saleItemKitId,
            kitCreateUuid: item.kitCreateUuid,

            promotionId: promotion?.id,
            skuTieredPriceId: tieredPrice?.id,
            priceTableId: priceTable.id,
            discountPriceTable: priceTable.discount,

            skuAssociatedId: item?.skuAssociatedId,

            choppGiftCardId: item.choppGiftCardId,
            palmBeerCardNumber: item.palmBeerCardNumber,

            quantity: item.quantity,

            originalUnitValue: priceInfo.originalPrice,
            unitValue: priceInfo.unitValue,
            discountType: priceInfo.discountType,
            unitDiscount: priceInfo.unitDiscount,
            contractualFine: priceInfo.contractualFine,

            customerRequired: item.skuProduct.customerRequired,
            inputMethod: item.inputMethod,
            isDeleted: item.isDeleted,
            isSuggestion: item.isSuggestion,
          }
        })

      const itemKits = items
        .filter(item => item.skuProduct.kit)
        .map(item => ({
          id: item.id,
          productKitId: item.skuProduct.productKitId,
          kitCreateUuid: item.kitCreateUuid,
          quantity: item.quantity,
          unitValue: item.priceRuleSelected.priceInfo.unitValue,
          isDeleted: item.isDeleted,
        }))

      const payments = paymentForm.receipts.map(r => ({
        id: r.id,
        paymentMethodId: r.paymentMethod.id,
        paymentMethodInterface: r.paymentMethodInterface,

        // Sequencial do pagamento na venda. NAO e a quantidade de parcelas do cartao.
        installment: 1,

        // Parcelamento do cartao. Ausente ou 1 significa a vista.
        installmentCount: Number(r.installmentCount) || 1,
        installmentMode: r.installmentMode,
        surchargeValue: r.surchargeValue || 0,

        value: r.value,
        canceled: r.canceled,
        cancelationActionType: r.cancelationActionType,
        change: r.change || 0,
        nsu: r.nsu,
        authorizationApprovalCode: r.authorizationApprovalCode,
        cardBrand: r.cardBrand,
        documentTitular: r.documentTitular?.length > 0 ? r.documentTitular : null,
        tefPayment: r.tefPayment,
        tefPaymentCancelation: r.tefPaymentCancelation,
        addedDate: r.addedDate,
        canceledDate: r.canceledDate,
        confirmationActionType: r.confirmationActionType,
        confirmedDate: r.confirmedDate,
        confirmedByUserId: r.confirmedByUserId,
      }))

      const deliveryData = {}

      if (isDelivery) {
        deliveryData.deliveryTax = deliveryForm.deliveryTax
        deliveryData.deliveryAddress = deliveryForm.address
        deliveryData.deliveryObservation = deliveryForm.observation
      }

      let invoiceData

      if (invoiceReturn) {
        invoiceData = {
          ...invoiceReturn,
          storeId: payBoxStore.id,
        }
      }

      // data to finish consigned sale
      let returnSale = null
      if (getters.isConsignedSaleFinish && returnForm.paymentMethod) {
        returnSale = {
          value: returnForm.value,
          paymentMethodId: returnForm.paymentMethod,
          observation: returnForm.observation,
        }
      }

      const itemsReturned = returnForm.returnedItems
        .filter(ri => ri.quantity > 0)
        .map(ri => ({
          saleItemId: ri.saleItemId,
          id: ri.itemReturnedId,
          skuId: ri.skuProduct.skuId,
          quantity: ri.quantity,
          unitValue: ri.unitValue,
        }))

      // Garante que a tabela default da loja esteja carregada em cache antes
      // de resolver a tabela efetiva. Cobre o caso de sessão restaurada onde
      // o bootstrap em `pay-box-configuration.js` pode não ter sido executado.
      await dispatch(
        'pages/pdv/effectivePriceTable/ensureDefaultPriceTable',
        { storeId: payBoxStore.id },
        { root: true }
      )

      // Em evento, `state.priceTable` é fixada com a tabela devolvida pelo
      // endpoint do evento e não pode cair para customer/default. Fora de evento,
      // mantém a resolução normal da tabela efetiva do canal.
      const effectiveSalePriceTable = eventId != null
        ? salePriceTable
        : rootGetters['pages/pdv/effectivePriceTable/effectivePriceTable'] ?? salePriceTable

      const data = {
        id: saleId,
        uuid,
        storeId: payBoxStore.id,
        eventId,
        payBoxId,
        priceTableId: effectiveSalePriceTable?.id ?? salePriceTable?.id,
        discountType: discountTypes.computed.discountTypesEnum().VALUE,
        discount: paymentForm.discount + paymentForm.rewardsAmountUsed,
        rewardsAmountUsed: paymentForm.rewardsAmountUsed,

        // Soma dos acrescimos de parcelamento repassados ao cliente. O acrescimo e cobrado no
        // pagamento e nao nos itens, entao nao entra no NetValue calculado a partir da venda: sem
        // informa-lo, o backend recusaria a venda como divergente entre total e pagamentos.
        installmentSurchargeValue: payments.reduce(
          (total, payment) => total + (payment.canceled ? 0 : payment.surchargeValue || 0),
          0
        ),
        comission,
        offline: false, // TODO hardcode offline
        customer,
        customerId: customer.id,
        items: saleItemsToSave,
        itemKits,
        invoiceSale: invoiceData || null,
        payments,
        returnSale,
        itemsReturned,
        applicationVersion: appVersions,
        coldOption,
        origin: isAtm ? 'SelfService' : origin,
        pickupPoint: pickupPoint || null,
        ...deliveryData,
      }

      if (getters.isConsignedSalePayment) {
        trackEvent('PayBoxSaleRecordPayment', {
          saleId: data.id,
          uuid: data.uuid,
          payBoxId: data.payBoxId,
          storeId: data.storeId,
        })

        await axiosInstance('/api/sales/pay-box-sale/record-payment', { method: 'PUT', data })

        return data
      }

      let orderPrint = null

      if (data.id) {
        const delegationToken = tempDelegations.find(delegation =>
          delegation.permissions?.some(p =>
            [
              delegablePermissions.PDV_SALE_RETURN_OVERFLOW_MAX_VALUE.permission,
              delegablePermissions.PDV_FINISH_ORDER_NOT_STORE_PRICE_TABLE.permission,
            ].includes(p)
          )
        )?.token

        await initMonitoring({ isPayBoxFinishOrderAction: true })
        trackEvent('PayBoxSale', {
          saleId: data.id,
          uuid: data.uuid,
          payBoxId: data.payBoxId,
          storeId: data.storeId,
        })

        await axiosInstance('/api/sales/pay-box-sale/finish-order', {
          method: 'PUT',
          data,
          headers: {
            'delegate-token': delegationToken,
          },
        })

        orderPrint = (await axiosInstance.get(`/api/sales/${data.id}`)).data
        try {
          this.busy = true

          await dispatch('pages/sale/order/printOrderExpedition', { orderPrint }, { root: true })
          await dispatch(
            'pages/pdv/payBoxPrint/printTicketItemsForEvent',
            { orderPrint },
            { root: true }
          )

          let templateToPrint = 'PRINT_INVOICE'

          if (orderPrint?.invoice?.model === 'NFE') {
            // É necessário imprimir como recibo pois foi gerada uma DANFe
            templateToPrint = 'SALE_ORDER'
          }

          // PRINT INVOICE SENDED
          await dispatch(
            'pages/sale/order/printOrder',
            { orderPrint, template: templateToPrint },
            { root: true }
          )

          if (getters.hasRentItems) {
            await dispatch(
              'pages/sale/order/printOrder',
              { orderPrint, template: 'SALE_RENT' },
              { root: true }
            )
          }

          if (getters.hasReturnSaleItems) {
            await dispatch(
              'pages/sale/order/printOrder',
              { orderPrint, template: 'SALE_RETURN' },
              { root: true }
            )
          }
        } catch (err) {
          console.error('print:', err)
        } finally {
          this.busy = false
        }
      } else {
        await initMonitoring({ isPayBoxSaleAction: true })
        trackEvent('PayBoxSale', {
          saleId: data.id,
          uuid: data.uuid,
          payBoxId: data.payBoxId,
          storeId: data.storeId,
        })

        const { data: saleReturn } = await axiosInstance('/api/sales/pay-box-sale', {
          method: 'POST',
          data,
        })
        if (saleReturn.id > 0) {
          try {
            orderPrint = (await axiosInstance.get(`/api/sales/${saleReturn?.id}`)).data

            await dispatch('pages/sale/order/printOrderExpedition', { orderPrint }, { root: true })

            await dispatch(
              'pages/pdv/payBoxPrint/printTicketItemsForEvent',
              { orderPrint },
              { root: true }
            )

            let templateToPrint = 'PRINT_INVOICE'

            if (orderPrint?.invoice?.model === 'NFE' || getters.isChoppGiftCardConversion) {
              // É necessário imprimir como recibo quando for gerada uma DANFe
              templateToPrint = 'SALE_ORDER'
            }

            // PRINT INVOICE SENDED
            await dispatch(
              'pages/sale/order/printOrder',
              {
                orderPrint,
                template: templateToPrint,
              },
              { root: true }
            )

            if (getters.hasLendingItems || getters.hasRentItems) {
              this.busy = true

              if (getters.hasLendingItems) {
                await dispatch(
                  'pages/sale/order/printOrder',
                  { orderPrint, template: 'LENDING_DELIVERY' },
                  { root: true }
                )

                await dispatch(
                  'pages/sale/order/printOrder',
                  { orderPrint, template: 'LENDING_CONTRACT' },
                  { root: true }
                )
              }

              if (getters.hasRentItems) {
                await dispatch(
                  'pages/sale/order/printOrder',
                  { orderPrint, template: 'SALE_RENT' },
                  { root: true }
                )
              }
            }
          } catch (err) {
            console.error('print:', err)
            this.busy = false
          }
        }
      }

      return orderPrint
    },

    async cancelIncompleteInvoiceSale({ state, dispatch, commit, rootGetters }) {
      await dispatch(
        'pages/pdv/payBoxConfiguration/checkCurrentPayBoxConfigAndFetch',
        {},
        {
          root: true,
        }
      )

      const { uuid, tempDelegations } = state

      const invoiceAgentConfigPayload =
        rootGetters['pages/pdv/payBoxConfiguration/invoiceAgentConfigPayload']

      const { store: payBoxStore, id: payBoxId } =
        rootGetters['pages/pdv/payBoxConfiguration/currentPayboxConfiguration']

      let detail = {}
      let model = ''
      let serialNumber = ''
      let success = false
      const invoiceNumber = state?.invoiceReturn?.invoiceNumber
      const status = state?.invoiceReturn?.status

      if (isElectron()) {
        if (
          state?.invoiceReturn?.model === 'NFE' &&
          state?.invoiceReturn?.status === 'SUCCESS_FINISHED'
        ) {
          const data = {
            id: state?.invoiceReturn?.id,
            eventType: 'CANCEL_INVOICE',
            justification: 'Cancelando nota devido a erro na finalização da venda',
          }

          const delegationInvoiceSave = tempDelegations.find(delegation =>
            delegation.permissions?.some(
              p => p === delegablePermissions.ERP_INVOICE_MANAGE.permission
            )
          )

          const { data: resultCancel } = await axiosInstance('/api/invoices/invoice-event', {
            method: 'POST',
            data,
            headers: {
              'delegate-token': delegationInvoiceSave?.token,
            },
          })

          if (resultCancel.status === 'SUCCESS_FINISHED') {
            success = true
            model = 'NFE'
            serialNumber = state?.invoiceReturn?.serialNumber

            state.electronicInvoiceInvoiceNumberGenerated = 0

            detail = {
              invoiceNumber: state?.invoiceReturn?.invoiceNumber,
              xmlPath: resultCancel.xml,
              returnMessage: resultCancel.returnMessage,
              returnCode: resultCancel.returnCode,
              accessKey: resultCancel.accessKey,
            }
            commit('SET_INVOICE_RETURN', null)
          }
        } else if (
          state?.invoiceReturn?.model === 'NFE' &&
          state?.invoiceReturn?.status === 'REJECTED'
        ) {
          const delegationInvoiceSave = tempDelegations.find(delegation =>
            delegation.permissions?.some(
              p => p === delegablePermissions.ERP_INVOICE_INVALIDATE.permission
            )
          )

          await axiosInstance('/api/invoices/invoice-invalidate', {
            method: 'POST',
            headers: {
              'delegate-token': delegationInvoiceSave?.token,
            },
            data: {
              storeId: state?.invoiceReturn.storeId,
              model: state?.invoiceReturn.model,
              serialNumber: state?.invoiceReturn.serialNumber,
              initialNumber: state?.invoiceReturn.invoiceNumber,
              finalNumber: state?.invoiceReturn.invoiceNumber,
              justification: 'Inutilizando nota devido a erro na finalização da venda',
            },
          })

          success = true
          model = 'NFE'
          serialNumber = state?.invoiceReturn?.serialNumber

          detail = {
            invoiceNumber: state?.invoiceReturn?.invoiceNumber,
            xmlPath: state?.invoiceReturn?.xml,
            returnMessage: state?.invoiceReturn?.returnMessage,
            returnCode: state?.invoiceReturn?.returnCode,
            accessKey: state?.invoiceReturn?.accessKey,
          }

          state.electronicInvoiceInvoiceNumberGenerated = 0
          commit('SET_INVOICE_RETURN', null)
        } else {
          const uuidPayload = {
            payBoxUuid: uuid,
          }

          const data = {
            ...invoiceAgentConfigPayload,
            request: JSON.stringify(uuidPayload),
          }

          const resultCancel = await window.electronAPI.invoice.cancelIncompleteInvoiceSale({
            ...data,
          })

          if (resultCancel) {
            success = true
            serialNumber = resultCancel.serialNumber
            commit('SET_INVOICE_RETURN', null)

            if (resultCancel.model === 'NFCE') {
              model = 'NFCE'
              detail = {
                xmlPath: resultCancel.xml,
                returnCode: resultCancel.returnCode,
                returnMessage: resultCancel.returnMessage,
                invoiceNumber: resultCancel.invoiceNumber,
                accessKey: resultCancel.accessKey,
              }
            } else if (resultCancel.model === 'SAT') {
              model = 'SAT'
              detail = {
                xmlPath: resultCancel.fileName,
                returnCode: resultCancel.code,
                returnMessage: resultCancel.message,
                invoiceNumber: resultCancel.cfeNumber,
                accessKey: resultCancel.accessKey,
              }
            }
          }
        }

        if (status === 'ISSUED_IN_CONTINGENCY') {
          const storeKey = `${payBoxStore.id}_${serialNumber}`

          await dispatch(
            'pages/pdv/payBoxConfiguration/updateLastInvoiceNumber',
            { invoiceNumber: invoiceNumber - 1 },
            { root: true }
          )

          await payBoxInvoiceNumberControlIdb.put({
            storekey: storeKey,
            invoiceNumber: invoiceNumber - 1,
          })
        }

        if (success) {
          const invoiceLog = {
            payBoxId,
            payBoxUuid: uuid,
            storeId: payBoxStore.id,
            model,
            eventType: 'CANCEL_INCOMPLETE_INVOICE_SALE',
            status: 'CANCELED',
            origin: 'PDV',
            serialNumber,
            ...detail,
          }

          await axiosInstance('/api/sales/pay-box/invoice-log', {
            method: 'POST',
            data: invoiceLog,
          })
        }
      }
    },

    // Atualiza o texto do overlay (se ativo) com o feedback enviado pelo Agent durante a emissão
    handleInvoiceMessage({ commit, rootGetters }, { key, args }) {
      if (!rootGetters['app/loadingOverlayShowActive']) return

      commit('app/SET_LOADING_OVERLAY', { show: true, text: i18n.t(key, args) }, { root: true })
    },

    async generateInvoice({ commit, state, rootState, rootGetters, dispatch, getters }) {
      const {
        store: payBoxStore,
        id: payBoxId,
        preferredInvoiceType,
      } = rootGetters['pages/pdv/payBoxConfiguration/currentPayboxConfiguration']

      // Não geramos cupom fiscal para conversão de cartão de presente, pois o cupom foi emitido na venda do cartão
      if (this.isChoppGiftCardConversion) {
        return {}
      }

      const {
        id: saleId,
        uuid,
        items,
        clientForm,
        paymentForm,
        deliveryForm,
        returnForm,
        comission,
        isDelivery,
      } = state
      const allItems = items
        .filter(i => !i.isDeleted && i.quantity > 0)
        .flatMap(i => (i.skuProduct.kit ? i.kitItems : [i]))
        .filter(i => !i.skuProduct.kit && i.quantity > 0)
      try {
        const invoiceItems = allItems
          .filter(
            item =>
              item.classification === 'Sale' ||
              item.classification === 'Credit' ||
              item.skuProduct.classification === 'Sale' ||
              item.skuProduct.classification === 'Credit'
          )
          .map(i => {
            const { priceInfo } = i.priceRuleSelected
            const unityPrice = priceInfo.unitValue

            return {
              productOrigin: i.skuProduct.tax.productOrigin,
              skuId: i.skuProduct.skuId,
              productName: i.skuProduct.name,
              unit: i.skuProduct.productUnit,
              cfop: i.skuProduct.tax.taxClassification.cfopOutput,
              ncm: i.skuProduct.tax.ncmCode,
              cest: i.skuProduct.tax.cest?.code || null,
              eanCode: i.skuProduct.ean,
              quantity: i.quantity,
              unityDiscountValue: priceInfo.unitDiscountValue,
              unityPrice,
              stIcms: i.skuProduct.tax.taxClassification.stIcms,
              icmsPercent: i.skuProduct.tax.taxClassification.icmsPercent,
              icmsBaseReductionPercent: i.skuProduct.tax.taxClassification.icmsBaseReductionPercent,
              stPis: i.skuProduct.tax.taxClassification.stPisOutput,
              pisPercent: i.skuProduct.tax.taxClassification.pisPercent,
              stCofins: i.skuProduct.tax.taxClassification.stCofinsOutput,
              cofinsPercent: i.skuProduct.tax.taxClassification.cofinsPercent,
              ibpt: i.skuProduct.tax.taxIbpt,
              benefitCode: i.skuProduct.tax.taxClassification.benefitCode,
              icmsTaxReliefReason: i.skuProduct.tax.taxClassification.icmsTaxReliefReason,

              stCbsIbsCode: i.skuProduct.tax.fiscalTaxParameter?.stCbsIbsCode,
              fiscalClassificationCbsIbsCode:
                i.skuProduct.tax.fiscalTaxParameter?.fiscalClassificationCbsIbsCode,
              cbsPercent: i.skuProduct.tax.fiscalTaxParameter?.cbsPercent,
              cbsReductionPercent: i.skuProduct.tax.fiscalTaxParameter?.cbsReductionPercent,
              cbsDeferralPercent: i.skuProduct.tax.fiscalTaxParameter?.cbsDeferralPercent,
              cbsValue: i.skuProduct.tax.fiscalTaxParameter?.cbsValue,
              ibsStatePercent: i.skuProduct.tax.fiscalTaxParameter?.ibsStatePercent,
              ibsStateReductionPercent:
                i.skuProduct.tax.fiscalTaxParameter?.ibsStateReductionPercent,
              ibsStateDeferralPercent: i.skuProduct.tax.fiscalTaxParameter?.ibsStateDeferralPercent,
              ibsStateValue: i.skuProduct.tax.fiscalTaxParameter?.ibsStateValue,
              ibsMunicipalPercent: i.skuProduct.tax.fiscalTaxParameter?.ibsMunicipalPercent,
              ibsMunicipalReductionPercent:
                i.skuProduct.tax.fiscalTaxParameter?.ibsMunicipalReductionPercent,
              ibsMunicipalDeferralPercent:
                i.skuProduct.tax.fiscalTaxParameter?.ibsMunicipalDeferralPercent,
              ibsMunicipalValue: i.skuProduct.tax.fiscalTaxParameter?.ibsMunicipalValue,
              stSelectiveTaxCode: i.skuProduct.tax.fiscalTaxParameter?.stSelectiveTaxCode,
              fiscalClassificationSelectiveTaxCode:
                i.skuProduct.tax.fiscalTaxParameter?.fiscalClassificationSelectiveTaxCode,
              selectiveTaxType: i.skuProduct.tax.fiscalTaxParameter?.selectiveTaxType,
              selectiveTaxPercent: i.skuProduct.tax.fiscalTaxParameter?.selectiveTaxPercent,
              selectiveTaxValuePerUnit:
                i.skuProduct.tax.fiscalTaxParameter?.selectiveTaxValuePerUnit,
              selectiveTaxValue: i.skuProduct.tax.fiscalTaxParameter?.selectiveTaxValue,
            }
          })

        const invoiceRentItems = allItems
          .filter(item => (item.classification || item.skuProduct.classification) === 'Rent')
          .map(i => ({
            productName: i.skuProduct.name,
            totalValue: i.priceRuleSelected.priceInfo.localNetValue,
          }))

        // no caso do consignado, como pode devolver um valor para o cliente, desconsideramos este valor dos pagamentos efetuados
        let totalReturned =
          returnForm?.returnedItems?.reduce(
            (partialSum, a) =>
              new Decimal(partialSum).add(a.priceRuleSelected.priceInfo.localNetValue).toNumber(),
            0
          ) ?? 0

        const paymentsDto = paymentForm.receipts
          .filter(p => p.canceled === false)
          .map(r => {
            let paymentValueAdjusted = new Decimal(totalReturned || 0).sub(r.value || 0)

            if (paymentValueAdjusted < 0) {
              totalReturned = 0
              paymentValueAdjusted = Math.abs(paymentValueAdjusted)
            } else {
              totalReturned = paymentValueAdjusted
              paymentValueAdjusted = 0
            }
            return {
              paymentType: r.paymentMethod.method,
              paymentMethodId: r.paymentMethod.id,
              paymentValue: paymentValueAdjusted,
              changeValue: r.change,
              paymentMethodInterface: r.paymentMethodInterface,
              nsu: r.nsu,
              authorizationApprovalCode: r.authorizationApprovalCode,
              cardBrand: r.cardBrand,
            }
          })
          .filter(p => p.paymentValue > 0)

        let customerDto

        const { E3 } = saleDomain.data().echopeClassificationEnum
        if (getters.salePriceTable?.echopeClassification !== E3 && clientForm.document) {
          customerDto = {
            id: clientForm.id,
            companyName: clientForm.name || null,
            document: clientForm.document,
          }

          if (
            isDelivery &&
            deliveryForm.address &&
            getters.clientHasValidAddressToInvoice(deliveryForm.address)
          ) {
            customerDto = {
              ...customerDto,
              address: { ...deliveryForm.address },
            }
          } else if (
            clientForm.address &&
            getters.clientHasValidAddressToInvoice(clientForm.address)
          ) {
            customerDto = {
              ...customerDto,
              address: { ...clientForm.address },
            }
          }
        }

        let deliveryAddress = null
        let deliveryObservation = null
        if (isDelivery && deliveryForm.address) {
          deliveryAddress = {
            publicPlace: deliveryForm.address.publicPlace,
            number: deliveryForm.address.number,
            complement: deliveryForm.address.complement,
            neighborhood: deliveryForm.address.neighborhood,
            zipcode: deliveryForm.address.zipcode,
            province: deliveryForm.address.province,
            provinceCode: deliveryForm.address.provinceCode,
            city: deliveryForm.address.city,
            cityCode: deliveryForm.address.cityCode,
          }
          deliveryObservation = deliveryForm.observation
        }

        const { payBox } = rootState.pages.pdv.payBoxConfiguration

        const storeKey = `${payBoxStore.id}_${payBox.nfce.serialNumber}`

        const lastStoredInvoiceNumber = await payBoxInvoiceNumberControlIdb.get(storeKey)

        let invoiceNumber = Number(payBox.nfce.lastInvoiceNumber ?? 0) + 1

        if (
          lastStoredInvoiceNumber &&
          Number(lastStoredInvoiceNumber.invoiceNumber ?? 0) + 1 > invoiceNumber
        ) {
          invoiceNumber = Number(lastStoredInvoiceNumber.invoiceNumber ?? 0) + 1
        }

        const invoice = {
          invoiceNumber,
          preferredInvoiceType,
          payBoxUuid: uuid,
          saleId: saleId?.toString(),
          store: payBoxStore,
          customer: customerDto,
          items: invoiceItems,
          itemsRent: invoiceRentItems,
          payments: paymentsDto,
          isDelivery: this.isDelivery,
          deliveryAddress,
          deliveryObservation,
          payBoxId,
          payBoxNumber: payBox.number,
          discountValue:
            paymentForm.discount +
            (deliveryForm.deliveryTaxDiscountValue || 0) +
            (paymentForm.rewardsAmountUsed || 0),
          freightValue: deliveryForm.deliveryTax,
          waiterComissionValue: comission,

          // Acréscimo do parcelamento no cartão. Vai como "outras despesas" da nota, no mesmo
          // lugar da gorjeta e do aluguel: é valor que o cliente paga sobre os itens, não está
          // em nenhum item, e o motor fiscal o rateia entre eles ao montar o vOutro que compõe
          // o vNF. Sem ele a soma dos pagamentos supera o total da nota, e a diferença é lida
          // como troco — recusada num pagamento em cartão.
          otherCostsValue: getters.getTotalInstallmentSurcharge,
        }

        const invoiceAgentConfigPayload =
          rootGetters['pages/pdv/payBoxConfiguration/invoiceAgentConfigPayload']

        const data = {
          ...invoiceAgentConfigPayload,
          request: JSON.stringify(invoice),
        }

        const result = await window.electronAPI.invoice.generateAndSubmit({ ...data })

        if (
          result &&
          (result.status === 'SUCCESS_FINISHED' || result.status === 'ISSUED_IN_CONTINGENCY')
        ) {
          commit('SET_INVOICE_RETURN', result)

          await dispatch('blockPayBoxActions', {
            event: payBoxTypes.data().uiPayBoxEventsEnum.SALE_FINISH,
          })

          if (result.model === 'NFCE') {
            await dispatch(
              'pages/pdv/payBoxConfiguration/updateLastInvoiceNumber',
              { invoiceNumber: result.invoiceNumber },
              { root: true }
            )
            await payBoxInvoiceNumberControlIdb.put({
              storekey: storeKey,
              invoiceNumber: result.invoiceNumber,
            })
          }

          let detail = {}

          if (result.model === 'NFCE') {
            detail = {
              xmlPath:
                result.status === 'ISSUED_IN_CONTINGENCY'
                  ? result.nfeDetail.xmlIssuedInContingencyPath
                  : result.nfeDetail.xmlFinishedSuccessPath,
              returnCode: result.nfeDetail.returnCode,
              returnMessage: result.nfeDetail.returnMessage,
              displayMessage: result.displayMessage,
              accessKey: result.nfeDetail.accessKey,
            }
          } else if (result.model === 'SAT') {
            detail = {
              xmlPath: result.satDetail.cfeXmlPath,
              returnCode: result.satDetail.returnCode,
              returnMessage: result.satDetail.returnMessage,
              displayMessage: result.displayMessage,
              accessKey: result.satDetail.cfeAccessKey,
            }
          }

          const invoiceLog = {
            payBoxId: result.payBoxId,
            payBoxUuid: result.payBoxUuid,
            storeId: result.store.id,
            model: result.model,
            invoiceNumber: result.invoiceNumber,
            eventType: 'SUBMIT_INVOICE',
            status: result.status,
            serialNumber: result.serialNumber,
            origin: 'PDV',
            ...detail,
          }

          await axiosInstance('/api/sales/pay-box/invoice-log', {
            method: 'POST',
            data: invoiceLog,
          })

          return detail
        }

        throw new Error(result.message)
      } catch (err) {
        console.error('error generating Customer Invoice', err)

        const getPayboxConfig = async () => {
          dispatch(
            'pages/pdv/payBoxConfiguration/fetchPayBox',
            {},
            {
              root: true,
            }
          ).catch(e => console.error('error on fetchPayBox', e))
        }

        // errors we can fix remote, and must refresh paybox config
        // 206 - Numero de NFe ja invalidado
        if (typeof err === 'string' && ['206'].some(e => err.startsWith(e))) {
          getPayboxConfig()
        }

        // errors we increment the sequence
        // 539 - Duplicidade de NFe com diferenca na Chave de Acesso
        if (typeof err === 'string' && ['539'].some(e => err.startsWith(e))) {
          const { payBox } = rootState.pages.pdv.payBoxConfiguration
          const invoiceNumber = Number(payBox.nfce.lastInvoiceNumber ?? 0) + 1
          await dispatch(
            'pages/pdv/payBoxConfiguration/updateLastInvoiceNumber',
            { invoiceNumber },
            { root: true }
          )
        }
        throw err
      }
    },

    async generateNFe({ commit, state, rootGetters, dispatch }) {
      const { store: payBoxStore, id: payBoxId } =
        rootGetters['pages/pdv/payBoxConfiguration/currentPayboxConfiguration']

      // Não geramos cupom fiscal para conversão de cartão de presente, pois o cupom foi emitido na venda do cartão
      if (this.isChoppGiftCardConversion) {
        return {}
      }

      const {
        uuid,
        items,
        clientForm,
        paymentForm,
        deliveryForm,
        returnForm,
        comission,
        tempDelegations,
      } = state
      const allItems = items
        .filter(i => !i.isDeleted && i.quantity > 0)
        .flatMap(i => (i.skuProduct.kit ? i.kitItems : [i]))
        .filter(i => !i.skuProduct.kit && i.quantity > 0)
      try {
        const invoiceItems = allItems
          .filter(
            item =>
              item.classification === 'Sale' ||
              item.classification === 'Credit' ||
              item.skuProduct.classification === 'Sale' ||
              item.skuProduct.classification === 'Credit'
          )
          .map(i => {
            const { priceInfo } = i.priceRuleSelected
            const unityPrice = priceInfo.unitValue

            const totalValue = new Decimal(unityPrice).mul(i.quantity).toNumber()

            return {
              productOrigin: i.skuProduct.tax.productOrigin,
              skuId: i.skuProduct.skuId,
              productName: i.skuProduct.name,
              unit: i.skuProduct.productUnit,
              cfop: i.skuProduct.tax.taxClassification.cfopOutput,
              ncm: i.skuProduct.tax.ncmCode,
              cest: i.skuProduct.tax.cest?.code || null,
              eanCode: i.skuProduct.ean,
              quantity: i.quantity,
              unityDiscountValue: priceInfo.unitDiscountValue,
              unityPrice,
              stIcms: i.skuProduct.tax.taxClassification.stIcms,
              icmsPercent: i.skuProduct.tax.taxClassification.icmsPercent,
              icmsBaseReductionPercent: i.skuProduct.tax.taxClassification.icmsBaseReductionPercent,
              stPis: i.skuProduct.tax.taxClassification.stPisOutput,
              stIpi: i.skuProduct.tax.taxClassification.stIpiOutput,
              pisPercent: i.skuProduct.tax.taxClassification.pisPercent,
              stCofins: i.skuProduct.tax.taxClassification.stCofinsOutput,
              cofinsPercent: i.skuProduct.tax.taxClassification.cofinsPercent,
              ibpt: i.skuProduct.tax.taxIbpt,
              benefitCode: i.skuProduct.tax.taxClassification.benefitCode,
              icmsTaxReliefReason: i.skuProduct.tax.taxClassification.icmsTaxReliefReason,
              productValue: totalValue,
              totalValue,

              stCbsIbsCode: i.skuProduct.tax.fiscalTaxParameter?.stCbsIbsCode,
              fiscalClassificationCbsIbsCode:
                i.skuProduct.tax.fiscalTaxParameter?.fiscalClassificationCbsIbsCode,
              cbsPercent: i.skuProduct.tax.fiscalTaxParameter?.cbsPercent,
              cbsReductionPercent: i.skuProduct.tax.fiscalTaxParameter?.cbsReductionPercent,
              cbsDeferralPercent: i.skuProduct.tax.fiscalTaxParameter?.cbsDeferralPercent,
              cbsValue: i.skuProduct.tax.fiscalTaxParameter?.cbsValue,
              ibsStatePercent: i.skuProduct.tax.fiscalTaxParameter?.ibsStatePercent,
              ibsStateReductionPercent:
                i.skuProduct.tax.fiscalTaxParameter?.ibsStateReductionPercent,
              ibsStateDeferralPercent: i.skuProduct.tax.fiscalTaxParameter?.ibsStateDeferralPercent,
              ibsStateValue: i.skuProduct.tax.fiscalTaxParameter?.ibsStateValue,
              ibsMunicipalPercent: i.skuProduct.tax.fiscalTaxParameter?.ibsMunicipalPercent,
              ibsMunicipalReductionPercent:
                i.skuProduct.tax.fiscalTaxParameter?.ibsMunicipalReductionPercent,
              ibsMunicipalDeferralPercent:
                i.skuProduct.tax.fiscalTaxParameter?.ibsMunicipalDeferralPercent,
              ibsMunicipalValue: i.skuProduct.tax.fiscalTaxParameter?.ibsMunicipalValue,
              stSelectiveTaxCode: i.skuProduct.tax.fiscalTaxParameter?.stSelectiveTaxCode,
              fiscalClassificationSelectiveTaxCode:
                i.skuProduct.tax.fiscalTaxParameter?.fiscalClassificationSelectiveTaxCode,
              selectiveTaxType: i.skuProduct.tax.fiscalTaxParameter?.selectiveTaxType,
              selectiveTaxPercent: i.skuProduct.tax.fiscalTaxParameter?.selectiveTaxPercent,
              selectiveTaxValuePerUnit:
                i.skuProduct.tax.fiscalTaxParameter?.selectiveTaxValuePerUnit,
              selectiveTaxValue: i.skuProduct.tax.fiscalTaxParameter?.selectiveTaxValue,
            }
          })

        const invoiceRentItems = allItems
          .filter(item => (item.classification || item.skuProduct.classification) === 'Rent')
          .map(i => ({
            productName: i.skuProduct.name,
            totalValue: i.priceRuleSelected.priceInfo.localNetValue,
          }))

        // no caso do consignado, como pode devolver um valor para o cliente, desconsideramos este valor dos pagamentos efetuados
        let totalReturned =
          returnForm?.returnedItems?.reduce(
            (partialSum, a) =>
              new Decimal(partialSum).add(a.priceRuleSelected.priceInfo.localNetValue).toNumber(),
            0
          ) ?? 0

        const paymentsDto = paymentForm.receipts
          .filter(p => p.canceled === false)
          .map(r => {
            let paymentValueAdjusted = new Decimal(totalReturned || 0).sub(r.value || 0)

            if (paymentValueAdjusted < 0) {
              totalReturned = 0
              paymentValueAdjusted = Math.abs(paymentValueAdjusted)
            } else {
              totalReturned = paymentValueAdjusted
              paymentValueAdjusted = 0
            }
            return {
              paymentType: r.paymentMethod.method,
              paymentMethodId: r.paymentMethod.id,
              paymentValue: paymentValueAdjusted,
              changeValue: r.change,
              paymentMethodInterface: r.paymentMethodInterface,
              nsu: r.nsu,
              authorizationApprovalCode: r.authorizationApprovalCode,
              cardBrand: r.cardBrand,
              isManual: r.paymentMethodInterface.toLowerCase() === 'manual',
            }
          })
          .filter(p => p.paymentValue > 0)

        let totalOtherCostsRent = 0
        let itensRentName = ''

        invoiceRentItems.forEach(rent => {
          totalOtherCostsRent += rent.totalValue
          itensRentName += `${rent.productName}, `
        })

        let additionalInformation = ''

        if (totalOtherCostsRent != null && totalOtherCostsRent > 0 && itensRentName !== '') {
          const stringItemsRent = `Foi adicionado um valor de ${totalOtherCostsRent} como acrescimo na venda referente ao aluguel dos itens: ${itensRentName.slice(
            0,
            -2
          )};`
          additionalInformation += stringItemsRent
        }

        if (comission != null && comission > 0) {
          const stringComission = `Foi adicionado um valor de ${comission} como acrescimo na venda referente a gorjeta do garçom;`
          additionalInformation += stringComission
        }

        const totalOtherCosts = totalOtherCostsRent + comission

        const bodyItems = {
          items: invoiceItems,
          discount: paymentForm.discount + (deliveryForm.deliveryTaxDiscountValue || 0),
          freigth: deliveryForm.deliveryTax,
          otherCosts: totalOtherCosts,
          insurance: 0,
          purposeType: 'NORMAL',
        }

        const delegationInvoiceSave = tempDelegations.find(delegation =>
          delegation.permissions?.some(
            p => p === delegablePermissions.ERP_INVOICE_GENERATE.permission
          )
        )

        const { data: itemsWithTax } = await axiosInstance('/api/invoices/calculate-item-tax', {
          method: 'POST',
          data: bodyItems,
          headers: {
            'delegate-token': delegationInvoiceSave?.token,
          },
        })

        const totalProductsValue = itemsWithTax.reduce(
          (total, item) => total + item.productValue,
          0
        )
        const totalValue = itemsWithTax.reduce((total, item) => total + item.totalValue, 0)
        const cfopCode = itemsWithTax[0].cfop

        const invoice = {
          invoiceNumber: state?.electronicInvoiceInvoiceNumberGenerated ?? 0,
          model: 'NFE',
          storeId: payBoxStore.id,
          serialNumber: payBoxStore.invoiceSerialNumber.toString(),
          customerId: clientForm.id,
          issueDate: moment().format(),
          movimentDate: moment().format(),
          movementType: 'OUTPUT',
          operationType: 'INTERNAL',
          invoiceCategory: 'SaleFromPdv',
          productsValue: totalProductsValue,
          freightValue: deliveryForm.deliveryTax,
          insuranceValue: 0,
          otherCostsValue: totalOtherCosts,
          discountValue:
            paymentForm.discount +
            (deliveryForm.deliveryTaxDiscountValue || 0) +
            (paymentForm.rewardsAmountUsed || 0),
          totalValue,
          items: itemsWithTax,
          payments: paymentsDto,
          purposeType: 'NORMAL',
          isFinalConsumer: true,
          presenceIndicator: 'PRESENTIAL_OPERATION',
          natureOperation: 'Venda de Mercadoria',
          paymentIndicator: 'OTHER',
          additionalInformation,
          cfopCode,
          saleUuid: uuid,
        }

        const { data: invoiceCreated } = await axiosInstance('/api/invoices', {
          method: 'POST',
          data: invoice,
          headers: {
            'delegate-token': delegationInvoiceSave?.token,
          },
        })

        let detail = {}

        if (!invoiceCreated) {
          console.error('Error on generating nfe', 'Ocorreu um erro ao emitir a nota fiscal!')
          throw new Error('Ocorreu um erro ao emitir a nota fiscal!')
        }
        try {
          this.loading = true

          const payload = {
            invoiceRequestType: 'GENERATE_AND_SEND_INVOICE',
            id: invoiceCreated.id,
            payBoxId,
          }

          const { data: invoiceResponse } = await axiosInstance('/api/invoices/invoice-manage', {
            method: 'POST',
            data: payload,
            headers: {
              'delegate-token': delegationInvoiceSave?.token,
            },
          })
          state.electronicInvoiceInvoiceNumberGenerated = invoiceResponse.invoiceNumber ?? 0

          if (invoiceResponse && invoiceResponse.status === 'SUCCESS_FINISHED') {
            commit('SET_INVOICE_RETURN', invoiceResponse)
            await dispatch('blockPayBoxActions', {
              event: payBoxTypes.data().uiPayBoxEventsEnum.SALE_FINISH,
            })

            detail = {
              xmlPath: invoiceResponse.nfeDetail.xmlFinishedSuccessPath,
              returnMessage: invoiceResponse.nfeDetail.returnMessage,
              displayMessage: invoiceResponse.displayMessage,
              accessKey: invoiceResponse.nfeDetail.accessKey,
            }
          } else if (invoiceResponse?.status === 'REJECTED') {
            commit('SET_INVOICE_RETURN', invoiceResponse)

            console.error('Nota fiscal rejeitada', invoiceResponse.nfeDetail.returnMessage)
            throw new Error(invoiceResponse.nfeDetail.returnMessage)
          } else if (invoiceResponse.status === 'WAITING_FOR_RESPONSE') {
            console.error(
              'Nota fiscal pendente de resposta da Sefaz. Aguarde alguns segundos e tente finalizar novamente para atualizar o status!'
            )
            throw new Error(
              'Nota fiscal pendente de resposta da Sefaz. Aguarde alguns segundos e tente finalizar novamente para atualizar o status!'
            )
          } else {
            console.error('error on generate nfe', invoiceResponse.nfeDetail.returnMessage)
            throw new Error(invoiceResponse.nfeDetail.returnMessage)
          }
        } catch (error) {
          console.error(error)
          throw error
        } finally {
          this.loading = false
        }
        return detail
      } catch (err) {
        console.error('error generating Invoice(NFe)', err)
        throw err
      }
    },

    async upsertIdbSession({ state, dispatch }) {
      try {
        if (state.isCleaning) {
          console.debug(`[upsertIdbSession] Skipped - cleanup in progress`)
          return
        }

        const { uuid } = state

        console.debug(`[upsertIdbSession] state uuid ${uuid}`)
        if (!uuid && (await dispatch('checkHasLastSession'))) {
          await dispatch('restoreIdbLastSession')
          return
        }
        const generateNewUuid = () => {
          const uuidSize = 20 // estamos usando 20 caracteres por no TEF ter esse limite no ID da venda
          let tries = 0 // evicting infinite loop
          do {
            const newUuid = nanoid(uuidSize)
            const lastUuid = localStorage.getItem('last_uuid_sale')
            if (!lastUuid || lastUuid !== newUuid) {
              localStorage.setItem('last_uuid_sale', newUuid)
              return newUuid
            }
            tries += 1
          } while (tries <= 20)

          return nanoid(uuidSize)
        }

        // const payload = JSON.stringify(state)
        const uuidGen = state.uuid ?? generateNewUuid()
        console.debug('[upsertIdbSession] USING uuid:', uuidGen, 'wasNull:', !state.uuid)
        await payBoxCurrentSaleIdb.put({ uuid: uuidGen, payload: { ...state, uuid: uuidGen } })
        state.uuid = uuidGen
        console.debug('[upsertIdbSession] SUCCESS - Saved to IDB, uuid:', state.uuid)
      } catch (err) {
        console.error(
          '[upsertIdbSession] ERROR saving to indexeddb, uuid:',
          state.uuid,
          'error:',
          err
        )
        throw err
      }
    },
    async checkHasLastSession() {
      try {
        const payBoxStorage = await payBoxCurrentSaleIdb.toArray()
        console.debug('[checkHasLastSession] payBoxStorage', payBoxStorage.length)
        if (payBoxStorage.length > 0) return true
        return false
      } catch (err) {
        console.error('store paybox restore from indexeddb', err)
        throw err
      }
    },
    /** Mesma regra do getter hasPayments, aplicada na venda salva no IndexedDB */
    async checkHasLastSessionWithPayments() {
      const [data] = await payBoxCurrentSaleIdb.limit(1).toArray()
      if (!data) return false

      const hasPayments = data.payload?.paymentForm?.receipts?.length > 0
      console.debug('[checkHasLastSessionWithPayments] uuid:', data.uuid, 'hasPayments:', hasPayments)
      return hasPayments
    },
    async restoreIdbLastSession({ commit, state, dispatch }) {
      let saleId = null
      try {
        // Previne restauração durante cleanup
        if (state.isCleaning) {
          console.debug('[restoreIdbLastSession] BLOCKED - cleanup in progress')
          return
        }

        console.debug('[restoreIdbLastSession] START - Current uuid:', state.uuid)
        const payBoxStorage = await payBoxCurrentSaleIdb.limit(1).toArray()
        const data = payBoxStorage[0]
        if (data) {
          console.debug('[restoreIdbLastSession] RESTORING - uuid from IDB:', data.uuid)
          if (typeof data.payload === 'string') {
            // #region // REMOVE pode ser removido quando todos os PDVs não tiverem mais no payload do indexedDB uma string de objeto
            const oldSaleDataFormatted = JSON.parse(data.payload)

            saleId = oldSaleDataFormatted.id || null

            const { returnedItems } =
              saleItemReturnedUtils.OldSaleItemReturnedToNewSaleItemReturned(
                oldSaleDataFormatted.returnForm.returnedItems
              )

            oldSaleDataFormatted.returnForm = { ...oldSaleDataFormatted.returnForm, returnedItems }

            oldSaleDataFormatted.itemForm = {
              ...oldSaleDataFormatted.itemForm,
              quantity: oldSaleDataFormatted.itemForm.amount,
            }

            oldSaleDataFormatted.items = oldSaleDataFormatted.items.map(oldSaleItem =>
              saleItemUtils.oldSaleItemToSaleItem(
                oldSaleItem,
                null,
                oldSaleDataFormatted?.returnForm?.returnedItems
              )
            )

            commit('SET_ALL_STATE', { ...oldSaleDataFormatted, uuid: data.uuid })
            console.debug(
              '[restoreIdbLastSession] SUCCESS (old format) - Restored uuid:',
              data.uuid
            )
            // #endregion
          } else {
            saleId = data.payload.id

            commit('SET_ALL_STATE', { ...data.payload })
            console.debug(
              '[restoreIdbLastSession] SUCCESS (new format) - Restored uuid:',
              data.payload?.uuid
            )
          }
        } else {
          console.debug('[restoreIdbLastSession] No data found in IDB')
        }

        if(saleId){
          try {
            await dispatch('payBoxSaleOrderLock', { saleId })
          } catch (err) {
            const errorData = getApiErrorData(err)
            const errorMessage = errorData.message || ''

            switch (errorMessage) {
              case 'SALE_ORDER.LOCK.SALE_ALREADY_LOCKED':
                await dispatch('cleanState')
                return

              case 'SALE_ORDER.NOT_FOUND':
              case 'SALE_ORDER.FINISH_ERROR.SALE_ALREADY_COMPLETED_OR_CANCELED':
                await dispatch('payBoxSaleOrderRelease', { saleId })
                await dispatch('cleanState')
                return

              default:
                throw err
            }
          }
        }
      } catch (err) {
        console.error('[restoreIdbLastSession] ERROR:', err)
        throw err
      }
    },
    async finishIdbSession({ state }) {
      try {
        console.debug('[finishIdbSession] START - Clearing uuid:', state.uuid)
        await payBoxCurrentSaleIdb.clear()
        const oldUuid = state.uuid
        state.uuid = null
        state.paymentStarted = false
        console.debug(
          '[finishIdbSession] SUCCESS - Cleared uuid:',
          oldUuid,
          '-> null, IDB count:',
          await payBoxCurrentSaleIdb.count()
        )
      } catch (err) {
        console.error('[finishIdbSession] ERROR - uuid:', state.uuid, 'error:', err)
        throw err
      }
    },

    AddTempDelegation({ commit }, delegation) {
      commit('ADD_TEMP_DELEGATIONS', delegation || {})
    },

    blockPayBoxActions({ commit, state }, { event }) {
      const {
        PAYMENT_ADD,
        PAYMENT_REMOVE,
        PRODUCT_ADD,
        PRODUCT_REMOVE,
        PRODUCT_UPDATE,
        SALE_ADD_DISCOUNT,
        SALE_UPDATE_DISCOUNT,
        SALE_APPLY_REWARD,
        SALE_REMOVE_REWARD,
        CUSTOMER_REMOVE,
      } = payBoxTypes.data().uiPayBoxActionsEnum
      const { SALE_FINISH } = payBoxTypes.data().uiPayBoxEventsEnum
      let blockedList = [...state.blockedPayBoxActions]

      if (event === SALE_FINISH) {
        blockedList = [
          ...blockedList,
          PAYMENT_ADD,
          PAYMENT_REMOVE,
          PRODUCT_ADD,
          PRODUCT_REMOVE,
          PRODUCT_UPDATE,
          SALE_ADD_DISCOUNT,
          SALE_UPDATE_DISCOUNT,
          SALE_APPLY_REWARD,
          SALE_REMOVE_REWARD,
          CUSTOMER_REMOVE,
        ]
      }

      const blockedListUniques = [...new Set(blockedList)]
      commit('SET_BLOCKED_PAY_BOX_ACTIONS', { actions: blockedListUniques })
    },
    unblockPayBoxActions({ commit }) {
      commit('SET_BLOCKED_PAY_BOX_ACTIONS', { actions: [] })
    },
    validatePayBoxActionIsBlocked({ state }, { action }) {
      const result = {
        isBlocked: false,
        title: 'PAYBOX.VIEW.BLOCKED_ACTIONS.TITLE',
        message: `PAYBOX.VIEW.BLOCKED_ACTIONS.${action}`,
      }

      if (state.blockedPayBoxActions.length === 0) return result

      result.isBlocked = state.blockedPayBoxActions.some(blocked => blocked === action)

      return result
    },

    cleanItemFormMeta({ commit }) {
      commit('CLEAN_ITEM_FORM_META')
    },
    async cleanClientForm({ commit }) {
      commit('CLEAN_CLIENT_FORM')
      // dispatch('pages/pdv/saleProducts/cleanProducts', null, { root: true })
    },
    async cleanPalmBeerData({ commit }) {
      commit('CLEAN_PALM_BEER_DATA')
    },
    async clearPayBoxRewardData({ dispatch, state }) {
      console.debug(
        '[clearPayBoxRewardData] START - uuid:',
        state.uuid,
        'rewardsAmountUsed:',
        state.paymentForm?.rewardsAmountUsed
      )
      await dispatch('reward/clearRewardData', {}, { root: true })

      // limpando o reward que fica na venda
      await dispatch('applyRewards', 0)
      console.debug('[clearPayBoxRewardData] SUCCESS - uuid:', state.uuid)
    },
    async cleanChoppGiftCardData({ commit }) {
      commit('CLEAN_CHOPP_GIFT_CARD_DATA')
    },
    async cleanClientFormWithoutDocument({ commit }) {
      commit('CLEAN_CLIENT_FORM_WITHOUT_DOCUMENT')
      // dispatch('pages/pdv/saleProducts/cleanProducts', null, { root: true })
    },
    async cleanState({ commit, dispatch, state }) {
      const oldUuid = state.uuid
      const itemsCount = state.items.length

      // Marca cleanup em progresso para evitar execução do updatePdvSession
      state.isCleaning = true
      console.debug(
        '[cleanState] START - uuid:',
        oldUuid,
        'items:',
        itemsCount,
        'isCleaning:',
        true
      )

      // Cancela qualquer updatePdvSession pendente do debounce
      await dispatch('cancelUpdatePdvSession')

      console.debug('[cleanState] Executing cleanup sequence...')
      await dispatch('pages/pdv/payBoxStatus/cleanPayBoxStatusState', {}, { root: true })
      await dispatch('finishIdbSession')
      commit('CLEAN_STATE')

      // Garante que isCleaning seja resetado após cleanup completo
      state.isCleaning = false
      console.debug(
        '[cleanState] SUCCESS - Cleanup complete, uuid:',
        oldUuid,
        '->',
        state.uuid,
        'isCleaning:',
        false
      )
    },
    async cancelIncompleteInvoice({ dispatch }) {
      await dispatch('cancelIncompleteInvoiceSale')
    },
    async payBoxSaleOrderLock({ rootGetters }, { saleId }) {
      const { id: PayBoxId } = rootGetters['pages/pdv/payBoxConfiguration/currentPayboxConfiguration']

      if (!saleId) return
      try {
        await axiosInstance.patch(`/api/sales/pay-box-sale/${saleId}/lock`, { PayBoxId })
      } catch (err) {
        const safeErrorInfo = {
          code: err?.code,
          statusCode: err?.response?.status,
          errorMessage: err?.message,
          responseData: err?.response?.data
        }
        console.error('[payBoxSaleOrderLock] Lock request failed:', safeErrorInfo)
        throw err
      }
    },
    async payBoxSaleOrderRelease({ state }, { saleId, type = 'unlock' }) {
      const id = saleId || state.id

      const endpointMap = {
        unlock: 'unlock',
        manual: 'manual-unlock'
      }

      try {
        await axiosInstance.patch(`/api/sales/pay-box-sale/${id}/${endpointMap[type]}`)
      } catch (err) {
        const safeErrorInfo = {
          code: err?.code,
          statusCode: err?.response?.status,
          errorMessage: err?.message
        }
        console.error('Error releasing sale order for PayBox', safeErrorInfo)
        throw err
      }
    },
  },
}
